<template>
  <div>
    <div class="row">
      <div class="col-6 d-flex align-items-center justify-content-lg-start">
        <Button
          v-if="showBackButton && actionType === 'save' || 'add'"
          :label="$t('List page')"
          size="md"
          icon="ArrowLeftIcon"
          show-icon
          class="mb-1"
          @click="backNavigationAction"
        />
      </div>
      <div
        v-if="actionType === 'add'"
        class="col-6 d-flex justify-content-end"
      >
        <Button
          v-if="showSearchButton"
          :label="$t('Search')"
          size="md"
          icon="SearchIcon"
          show-icon
          @click="showSearchField"
        />
      </div>
    </div>
    <b-card v-if="!showSearchButton && !showVirtualAccountWizard">
      <b-row class="position-relative">
        <b-col
          md="4"
        >
          <validation-observer ref="searchRules">
            <b-form @submit.prevent="handleSearch('search')">
              <b-row>
                <Alert
                  :show="showFormErrorAlert"
                  :message="$t('Customer\'s account name or account number or customer id is required')"
                />

                <b-col md="12">
                  <InputField
                    id="customer-id"
                    v-model="searchParams.customerID"
                    :label="$t('Customer ID')"
                    :placeholder="$t('Enter customer id')"
                    type="text"
                  />
                </b-col>

                <b-col md="12">
                  <InputField
                    id="customer-name"
                    v-model="searchParams.name"
                    :label="$t('Customer\'s Name')"
                    :placeholder="$t('Enter customer\'s name')"
                    type="text"
                  />
                </b-col>

                <b-col md="12">
                  <InputField
                    id="email-address"
                    v-model="searchParams.email"
                    :label="$t('Customer\'s Email')"
                    :placeholder="$t('Enter email address')"
                    type="email"
                  />
                </b-col>

                <b-col md="12">
                  <Button
                    :label="$t('Search')"
                    size="md"
                    class="mt-2"
                    block
                    @click="handleSearch('search')"
                  />
                </b-col>
              </b-row>
            </b-form>
          </validation-observer>
        </b-col>
      </b-row>
    </b-card>
    <!-- Virtual Account -->
    <form-wizard
      v-if="showVirtualAccountWizard"
      ref="virtualAccountForm"
      :title="null"
      :subtitle="null"
      layout="vertical"
      :back-button-text="$t('Previous')"
      class="wizard-vertical mb-3"
    >

      <!-- Account Information tab -->
      <tab-content
        :title="$t('Account Information')"
      >
        <validation-observer ref="accountInformationRules">
          <b-form>
            <h5 class="section-label mb-2">
              {{ $t('Account Information') }}
            </h5>
            <b-row class="match-height">
              <b-col md="6">
                <InputField
                  id="parent-customer-id"
                  v-model="virtualAccountFormData.customerID"
                  :label="$t('Parent Customer ID')"
                  type="text"
                  readonly
                />
              </b-col>

              <b-col md="6">
                <InputField
                  id="parent-account-name"
                  v-model="virtualAccountFormData.accountName"
                  :label="$t('Parent Account Name')"
                  type="text"
                  readonly
                />
              </b-col>
              <b-col md="6">
                <InputField
                  id="parent-account-number"
                  v-model="virtualAccountFormData.parentAccountNumber"
                  :label="$t('Parent Account Number')"
                  type="text"
                  readonly
                />
              </b-col>

              <b-col md="6">
                <InputField
                  id="last-name"
                  v-model="virtualAccountFormData.lastName"
                  :label="$t('Last Name')"
                  type="text"
                  @input="setAccountLastName"
                />
              </b-col>
              <b-col md="6">
                <InputField
                  id="first-name"
                  v-model="virtualAccountFormData.firstName"
                  :label="$t('First Name')"
                  type="text"
                  @input="setAccountFirstName"
                />
              </b-col>
              <b-col md="6">
                <InputField
                  id="other-names"
                  v-model="virtualAccountFormData.otherNames"
                  :label="$t('Other Names')"
                  type="text"
                  @input="setAccountOtherNames"
                />
              </b-col>
              <b-col md="6">
                <InputField
                  id="phone-number"
                  v-model="virtualAccountFormData.phoneNumber"
                  :label="$t('Phone Number')"
                  type="text"
                />
              </b-col>
              <b-col md="6">
                <InputField
                  id="email"
                  v-model="virtualAccountFormData.email"
                  :label="$t('Email')"
                  type="email"
                />
              </b-col>

              <b-col md="6">
                <SelectField
                  id="gender"
                  v-model="virtualAccountFormData.accountType"
                  :label="$t('Account Type')"
                  :placeholder="$t('Select account type')"
                  :options="virtualAccountTypeOption"
                  :reduce="option => option.value"
                  item="label"
                />
              </b-col>

              <b-col md="6">
                <InputField
                  id="virtual-account-name"
                  v-model="virtualAccountFormData.virtualAccountName"
                  :label="$t('Virtual Account Name')"
                  type="text"
                  readonly
                />
              </b-col>

              <b-col md="6">
                <SelectField
                  id="branch"
                  v-model="virtualAccountFormData.branchCode"
                  :label="$t('Branch')"
                  :reduce="option => option.value"
                  :options="branchOptions"
                  item="label"
                  readonly
                />
              </b-col>

              <b-col md="6">
                <SelectField
                  id="product-type"
                  v-model="virtualAccountFormData.productCode"
                  :label="$t('Product Type')"
                  :reduce="option => option.value"
                  :options="productTypeOptions"
                  item="label"
                />
              </b-col>
              <b-col md="6">
                <SelectField
                  id="account-officer"
                  v-model="virtualAccountFormData.accountOfficerID"
                  :label="$t('Account Officer')"
                  :reduce="option => option.value"
                  :options="accountOfficerOptions"
                  item="label"
                  readonly
                />
              </b-col>

              <b-col md="6">
                <SelectField
                  id="account-tier-level"
                  v-model.number="virtualAccountFormData.accountTierLevel"
                  :label="$t('Account Tier Level')"
                  :options="accountTierOptions"
                  item="label"
                  :reduce="option => option.value"
                  readonly
                />
              </b-col>

              <b-col md="6">
                <InputField
                  id="bvn"
                  v-model="virtualAccountFormData.bvn"
                  :label="$t('BVN')"
                  type="text"
                />
              </b-col>
              <b-col md="6">
                <InputField
                  id="nin"
                  v-model="virtualAccountFormData.nin"
                  :label="$t('NIN')"
                  type="text"
                />
              </b-col>
            </b-row>
          </b-form>
        </validation-observer>
      </tab-content>

      <!-- Personal Information tab -->
      <tab-content
        :title="$t('Personal Information')"
      >
        <validation-observer ref="personalInfoRules">
          <b-form>
            <b-row class="match-height">
              <b-col md="3">
                <InputField
                  id="bvn"
                  v-model="virtualAccountFormData.bvn"
                  :label="$t('BVN')"
                  :placeholder="$t('Enter BVN')"
                  type="text"
                  :error-name="$t('BVN')"
                  rules="integer|length:11"
                />
              </b-col>
              <b-col md="3">
                <Button
                  v-if="getUserPermission('VALIDATE_BVN')"
                  :label="$t('Validate BVN')"
                  :loading="isLoading"
                  size="md"
                  class="mt-2 btn-text-right"
                  @click="bvnValidateAction"
                />
              </b-col>
              <b-col md="6">
                <SelectField
                  id="title"
                  v-model="virtualAccountFormData.title"
                  :label="$t('Title')"
                  :options="titleOptions"
                  :reduce="option => option.value"
                  :error-name="$t('Title')"
                  rules="required"
                  item="label"
                  :placeholder="$t('Select')"
                />
              </b-col>

              <!-- Last Name -->
              <b-col md="6">
                <InputField
                  id="last-name"
                  v-model="virtualAccountFormData.lastName"
                  :label="$t('Last Name')"
                  :placeholder="$t('Enter last name')"
                  type="text"
                  :error-name="$t('Last Name')"
                  rules="required"
                  onpaste="return false;"
                  :is-allow-special-characters="false"
                />
              </b-col>
              <!-- Last Name -->

              <!-- First Name -->
              <b-col md="6">
                <InputField
                  id="first-name"
                  v-model="virtualAccountFormData.firstName"
                  :label="$t('First Name')"
                  :placeholder="$t('Enter first name')"
                  type="text"
                  :error-name="$t('First Name')"
                  rules="required"
                  onpaste="return false;"
                  :is-allow-special-characters="false"
                />
              </b-col>
              <!-- First Name -->

              <!-- Other Name -->
              <b-col md="6">
                <InputField
                  id="other-names"
                  v-model="virtualAccountFormData.otherNames"
                  :label="$t('Other Names')"
                  :placeholder="$t('Enter other names')"
                  type="text"
                  :error-name="$t('Other Names')"
                  onpaste="return false;"
                  :is-allow-special-characters="false"
                />
              </b-col>
              <!-- Other Name -->

              <!-- Maiden Name -->
              <b-col md="6">
                <InputField
                  id="maiden-name"
                  v-model="virtualAccountFormData.maidenName"
                  :label="$t('Maiden Name')"
                  :placeholder="$t('Enter maiden name')"
                  type="text"
                  :error-name="$t('Maiden Name')"
                />
              </b-col>
              <!-- Maiden Name -->

              <!-- Gender -->
              <b-col md="6">
                <SelectField
                  id="gender1"
                  v-model="virtualAccountFormData.gender"
                  :label="$t('Gender')"
                  :options="genderOptions"
                  :reduce="option => option.value"
                  item="label"
                  :error-name="$t('Gender')"
                  rules="required"
                  :placeholder="$t('Select')"
                />

              </b-col>
              <!-- Gender -->
              <!-- Marital Status -->
              <b-col md="6">
                <SelectField
                  id="marital-status"
                  v-model="virtualAccountFormData.maritalStatus"
                  :label="$t('Marital Status')"
                  :options="maritalStatusOptions"
                  :reduce="option => option.value"
                  :error-name="$t('Marital Status')"
                  rules="required"
                  item="label"
                  :placeholder="$t('Select')"
                />

              </b-col>
              <!-- Marital Status -->

              <!-- Phone Number -->
              <b-col md="6">
                <InputField
                  id="phone-number"
                  v-model="virtualAccountFormData.phoneNumber"
                  :label="$t('Phone Number')"
                  :placeholder="$t('Enter phone number')"
                  type="number"
                  :error-name="$t('Phone Number')"
                  rules="required|integer|min:7|max:13"
                  @input="setPhoneNumber"
                />
              </b-col>
              <!-- Phone Number -->

              <!-- Secondary Phone Numbers -->
              <b-col md="6">
                <TextAreaField
                  id="sec-phone-number"
                  v-model="virtualAccountFormData.notificationPhoneNumber"
                  :label="$t('Notification Phone Number')"
                  :placeholder="$t('For multiple Phone Numbers, separate by comma (,)')"
                  type="text"
                  for="sec-phone-number"
                  :error-name="$t('Phone Number')"
                  rules="required"
                  rows="2"
                  show-icon
                  :on-click="onViewPhoneInfo"
                />
              </b-col>
              <!-- Secondary Phone Number -->

              <!-- Email -->
              <b-col md="6">
                <InputField
                  id="email"
                  v-model="virtualAccountFormData.email"
                  :label="$t('Email')"
                  :placeholder="$t('Enter email')"
                  type="email"
                  @input="setEmailAddress"
                />
              </b-col>
              <!--  Email -->

              <!-- Secondary Email -->
              <b-col md="6">
                <TextAreaField
                  id="sec-email"
                  v-model="virtualAccountFormData.notificationEmail"
                  :label="$t('Notification Email')"
                  :placeholder="$t('For multiple Emails, separate by comma (,)')"
                  type="text"
                  :error-name="$t('Email')"
                  rows="2"
                  show-icon
                  :on-click="onViewEmailInfo"
                />
              </b-col>
              <!-- Secondary Email -->

              <!-- Date of Birth -->
              <b-col md="6">
                <DatePickerField
                  id="date-of-birth"
                  v-model="virtualAccountFormData.dateOfBirth"
                  rules="required"
                  :label="$t('Date of Birth')"
                  format="Y-m-d"
                  :error-name="$t('Date of Birth')"
                  :config="datePickerConfig"
                  :disabled="isReadonly"
                />
              </b-col>
              <!--  Date of Birth -->

              <!-- Country -->
              <b-col md="6">
                <SelectField
                  id="country"
                  v-model="virtualAccountFormData.nationality"
                  :label="$t('Country')"
                  :options="countryOptions"
                  :reduce="option => option"
                  :error-name="$t('Country')"
                  rules="required"
                  item="label"
                  :placeholder="$t('Select')"
                />
              </b-col>
              <!--  Country -->

              <!-- State -->
              <b-col md="6">
                <SelectField
                  id="state"
                  v-model="virtualAccountFormData.state"
                  :label="$t('State/Province')"
                  :options="stateOptions"
                  :reduce="option => option"
                  :error-name="$t('State')"
                  rules="required"
                  item="label"
                  :placeholder="$t('Select')"
                  :set-selected="getLGAByState"
                />

              </b-col>
              <!--  State -->

              <!-- LGA/Cities -->
              <b-col md="6">
                <SelectField
                  id="state"
                  v-model="virtualAccountFormData.lga"
                  :label="$t('LGA/Cities')"
                  :options="lgaOptions"
                  :reduce="option => option"
                  :error-name="$t('lga')"
                  rules="required"
                  item="label"
                  :placeholder="$t('Select')"
                />
              </b-col>
              <!--  LGA -->

              <!-- Home Town -->
              <b-col md="6">
                <InputField
                  id="home-town"
                  v-model="virtualAccountFormData.hometown"
                  :label="$t('Home Town')"
                  :placeholder="$t('Enter home town')"
                  type="text"
                  :error-name="$t('Home Town')"
                  rules="required"
                />
              </b-col>
              <!-- Home Town -->

              <!-- Religion -->
              <b-col md="6">
                <SelectField
                  id="religion"
                  v-model="virtualAccountFormData.religion"
                  :label="$t('Religion')"
                  :options="religionOptions"
                  :reduce="option => option.value"
                  :error-name="$t('Religion')"
                  rules="required"
                  item="label"
                  :placeholder="$t('Select')"
                />
              </b-col>
              <!--  Religion -->

              <!--  Means of Identification -->
              <b-col md="6">
                <SelectField
                  id="means-of-id"
                  v-model="virtualAccountFormData.identificationType"
                  :label="$t('Means of Identification')"
                  :options="meansOfIdOptions"
                  :reduce="option => option.value"
                  item="label"
                  :placeholder="$t('Select')"
                />
              </b-col>
              <!--  Means of Identification -->

              <!-- Means of Identification Number -->
              <b-col md="6">
                <InputField
                  id="id-number"
                  v-model="virtualAccountFormData.identificationNumber"
                  :label="$t('Means of Identification Number')"
                  :placeholder="$t('Enter identification number on your card')"
                  type="text"
                />
              </b-col>
              <!-- Means of Identification Number -->
              <!--  PEP -->
              <b-col md="6">
                <SelectField
                  id="pep"
                  v-model="virtualAccountFormData.pep"
                  :label="$t('PEP')"
                  :options="pepValueOptions"
                  :reduce="option => option.value"
                  :error-name="$t('PEP')"
                  rules="required"
                  item="label"
                  :placeholder="$t('Select')"
                />
              </b-col>
              <!--  PEP -->
              <!-- Tin -->
              <b-col md="6">
                <InputField
                  id="tin"
                  v-model="virtualAccountFormData.tin"
                  :label="$t('TIN')"
                  :placeholder="$t('Enter TIN')"
                  type="text"
                />
              </b-col>
              <!-- Tin -->

              <!--  Old customer ID -->
              <b-col md="6">
                <InputField
                  id="reference-number"
                  v-model="virtualAccountFormData.oldCustomerID"
                  :label="$t('Old customer ID')"
                  :placeholder="$t('Enter old customer ID')"
                  type="text"
                  show-icon
                  :on-click="onViewInfo"
                />
              </b-col>
              <!--  Old customer ID -->

              <!--  IPPIS/Oracle ID -->
              <b-col md="6">
                <InputField
                  id="ippis-oracle"
                  v-model="virtualAccountFormData.employeeID"
                  :label="$t('IPPIS/Oracle ID')"
                  :placeholder="$t('Enter IPPIS/Oracle ID')"
                  type="text"
                />
              </b-col>
              <!--  IPPIS/Oracle ID -->

              <!--  Address -->
              <b-col md="12">
                <TextAreaField
                  id="address"
                  v-model="virtualAccountFormData.address"
                  :label="$t('Address')"
                  :placeholder="$t('Enter address')"
                  :error-name="$t('Address')"
                  rules="required"
                  for="address"
                  rows="5"
                />
              </b-col>
            </b-row>
          <!--  Address -->
          </b-form>
        </validation-observer>
      </tab-content>

      <!-- Other Documents tab-->
      <tab-content
        :title="$t('Other Documents')"
        :before-change="validatUploads"
      >
        <validation-observer ref="uploadRules">
          <b-form>
            <div>
              <div class="w-full mt-4 p-10">
                <div class="d-flex mb-0 mt-2 align-items-center justify-content-between">
                  <h4>Other Documents Upload</h4>

                  <b-button
                    v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                    variant="primary"
                    class="ml-4"
                    @click="addNewDocument()"
                  >
                    <feather-icon
                      icon="PlusIcon"
                      class="mr-25"
                    />
                    <span>{{ $t('Add New Document') }}</span>
                  </b-button>
                </div>
                <b-row
                  v-for="(item, index) in documentsFormData"
                  :id="index"
                  :key="index"
                  class="mb-2"
                >
                  <b-col md="6">
                    <SelectField
                      id="document-type"
                      v-model="item.type"
                      :reduce="option => option.value"
                      :label="$t('Document Type')"
                      :placeholder="$t('Select')"
                      :options="translatedDocumentTypeOptions"
                      :error-name="$t('Document Type')"
                      rules="required"
                      item="label"
                    />
                  </b-col>
                  <b-col
                    md="7"
                  >
                    <label>{{ $t('Upload Documents') }}</label>

                    <div class="upload-image">
                      <Spinner :show="documentLoadSpinner" />
                      <b-form-group>
                        <b-form-file
                          :placeholder="$t('Choose a file or drop it here...')"
                          :drop-placeholder="$t('Drop file here...')"
                          accept="image/jpeg, image/png, image/jpg, application/pdf, .doc, .docx, application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                          plain
                          @change="onDocumentsUpload(index, $event)"
                        />
                        <div
                          v-if="!item.url"
                          class="upload-instruction"
                        >
                          <h4>{{ $t('Upload Document File') }}</h4>
                          <p>{{ $t('Drag & drop file or choose from your device to upload') }}</p>
                          <p class="format">
                            {{ $t('Format accepted') }} (jpg, png, jpeg, docx, pdf)
                          </p>
                        </div>
                        <div v-else>
                          <div v-if="item.fileExtension === 'png'|| item.fileExtension === 'jpeg'|| item.fileExtension === 'jpg'">
                            <b-img
                              :src="item.url"
                              block
                              rounded
                              width="250px"
                              height="250px"
                            />
                          </div>
                          <div
                            v-if="item.fileExtension === 'pdf'"
                          >
                            <b-img
                              :src="pdfIcon"
                              block
                              rounded
                              width="250px"
                              height="250px"
                            />
                          </div>
                          <div
                            v-else-if="item.fileExtension === 'vnd'"
                          >
                            <b-img
                              :src="wordIcon"
                              block
                              rounded
                              width="250px"
                              height="250px"
                            />
                          </div>
                          <div
                            v-else
                          >
                            <b-img
                              src=""
                            />
                          </div>
                          <div
                            v-if="item.url"
                            class="trash"
                          >
                            <feather-icon
                              icon="Trash2Icon"
                              size="20"
                              class="text-primary"
                              @click="deleteDocumentFile(item.id, index)"
                            />
                          </div>
                          <div
                            v-if="item.url"
                            class="view-details"
                          >
                            <feather-icon
                              icon="EyeIcon"
                              size="20"
                              class="text-primary"
                              @click="viewDocumentFile(item.url, item.fileName, item.fileExtension)"
                            />
                          </div>
                          <!-- </div> -->
                        </div>
                      </b-form-group>
                    </div>
                  </b-col>
                  <b-col
                    md="3"
                    class="mt-0"
                  >
                    <b-row>
                      <!-- Remove Button -->
                      <b-col
                        v-if="item.url"
                        md="12"
                        class="mt-1"
                      >
                        <b-button
                          v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                          variant="primary"
                          class="ml-0"
                          @click="removeDocument(item.id, index)"
                        >
                          <feather-icon
                            icon="XIcon"
                            class="mr-25"
                          />
                          <span>{{ $t('Remove') }}</span>
                        </b-button>
                      </b-col>
                      <b-col
                        v-if="item.url"
                        md="12"
                        class="mt-1"
                      >
                        <b-button
                          v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                          variant="primary"
                          class="ml-0"
                          @click="handleDownload(item.url)"
                        >
                          <feather-icon
                            icon="DownloadIcon"
                            class="mr-25"
                          />
                          <span>{{ $t('Download') }}</span>
                        </b-button>
                      </b-col>
                    </b-row>
                  </b-col>
                </b-row>
              </div>
            </div>
          </b-form>
        </validation-observer>
      </tab-content>

      <template
        slot="finish"
      >
        <div class="wizard-footer-right">
          <Button
            :label="$t('Save')"
            :loading="isLoading"
            :disabled="isDisabled"
            size="lg"
            @click="handleVirtualAccount"
          />
        </div>

      </template>
      <FullScreenSpinner :show="showSpinner" />
    </form-wizard>
    <!-- Virtual Account -->

    <form-wizard
      v-if="showSearchButton && !showVirtualAccountWizard"
      ref="casaAccount"
      :title="null"
      :subtitle="null"
      layout="vertical"
      :back-button-text="$t('Previous')"
      :next-button-text="$t('Next')"
      class="wizard-vertical mb-3"
    >
      <tab-content
        :title="$t('Account Information')"
        :before-change="validateAccountInformation"
      >
        <validation-observer ref="savingRules">
          <b-form>
            <b-row class="">
              <b-col md="4">
                <InputField
                  id="customer-id"
                  v-model="customer.customerID"
                  :label="$t('Customer ID')"
                  :placeholder="$t('Enter customer id')"
                  type="text"
                  readonly
                />
              </b-col>

              <b-col md="4">
                <InputField
                  id="account-name"
                  v-model="customer.accountName"
                  :label="$t('Account Name')"
                  :placeholder="$t('Enter account name')"
                  type="text"
                  rules="required"
                  onpaste="return false;"
                  :is-allow-special-characters="false"
                />
              </b-col>

              <b-col md="4">
                <SelectField
                  id="account-type"
                  v-model="customer.accountType"
                  :label="$t('Account Type')"
                  :options="customer.accountType === 'VirtualProxy' || customer.accountType === 'VirtualWallet' ? virtualAccountsTypeOption : accountTypeOptions"
                  :reduce="option => option.value"
                  rules="required"
                  :error-name="$t('Account Type')"
                  :readonly="isReadonly"
                  item="label"
                  :placeholder="$t('Select account type')"
                />
              </b-col>
              <b-col md="4">
                <SelectField
                  id="branch"
                  v-model="customer.branchCode"
                  :label="$t('Branch')"
                  required="true"
                  :error-name="$t('Branch')"
                  :reduce="option => option.value"
                  :options="branchOptions"
                  item="label"
                  :readonly="customer.accountType === 'VirtualProxy' || customer.accountType === 'VirtualWallet' ? isEditable : isReadonly"
                  :placeholder="$t('Select branch')"
                  rules="required"
                  :set-selected="setCustomerBranch"
                />
              </b-col>

              <b-col md="4">
                <SelectField
                  id="product-type"
                  v-model="customer.productCode"
                  :label="$t('Product Type')"
                  required="true"
                  :error-name="$t('Product Type')"
                  :reduce="option => option.value"
                  :options="productTypeOptions"
                  item="label"
                  :placeholder="$t('Select product type')"
                  :set-selected="setApplicableInterest"
                  rules="required"
                />
              </b-col>
              <b-col md="4">
                <SelectField
                  id="account-officer"
                  v-model="customer.accountOfficerID"
                  :label="$t('Account Officer')"
                  required="true"
                  :error-name="$t('Account Officer')"
                  :options="accountOfficerOptions"
                  item="label"
                  :reduce="option => option.value"
                  :placeholder="$t('Select account officer')"
                  rules="required"
                  :set-selected="setAccountOfficerName"
                />
              </b-col>
              <b-col
                v-if="actionType !== 'add'"
                md="4"
              >
                <SelectField
                  id="account-tier-level"
                  v-model.number="customer.accountStatus"
                  :label="$t('Account Status')"
                  :error-name="$t('Account Status')"
                  :options="accountStatusOptions"
                  item="label"
                  :reduce="option => option.value"
                  :placeholder="$t('Select account status')"
                  rules="required"
                  readonly
                />
              </b-col>
              <b-col md="4">
                <SelectField
                  id="account-tier-level"
                  v-model.number="customer.accountTierLevel"
                  :label="$t('Account Tier Level')"
                  :error-name="$t('Account Tier Level')"
                  :options="accountTierOptions"
                  item="label"
                  :reduce="option => option.value"
                  :placeholder="$t('Select account tier level')"
                  rules="required"
                />
              </b-col>
              <b-col md="4">
                <SelectField
                  id="access-level"
                  v-model.number="customer.accessLevel"
                  :label="$t('Access Level')"
                  :reduce="option => option.value"
                  rules="required"
                  :error-name="$t('Access Level')"
                  :options="accessLevelOptions"
                  item="label"
                  :placeholder="$t('Select access level')"
                />
              </b-col>
              <!--  Sector Code -->
              <b-col md="4">
                <SelectField
                  id="sector-code"
                  v-model="customer.sectorCode"
                  :label="$t('Sector Code')"
                  :options="sectorCodeOptions"
                  :reduce="option => option.value"
                  item="label"
                  :placeholder="$t('Select')"
                  :readonly="isReadonly"
                  rules="required"
                  :error-name="$t('Sector Code')"
                />
              </b-col>
              <!--  Sector Code -->
              <!--  Category of Account -->
              <b-col md="4">
                <SelectField
                  id="category-of-account"
                  v-model="customer.categoryOfAccount"
                  :label="$t('Category of Account')"
                  :options="categoryOfAccountOptions"
                  :reduce="option => option.value"
                  item="label"
                  :placeholder="$t('Select')"
                  :readonly="isReadonly"
                  rules="required"
                />
              </b-col>
              <!--  Category of Account -->
              <b-col md="4">
                <InputNumberField
                  id="minimum-balance"
                  v-model.number="customer.minimumBalanceRequired"
                  :label="$t('Minimum Balance Required')"
                  :placeholder="$t('Enter minimum balance required')"
                  :readonly="isReadonly"
                />
              </b-col>

              <b-col md="4">
                <SelectField
                  id="currency-code"
                  v-model="customer.currencyCode"
                  :label="$t('Currency')"
                  :reduce="option => option"
                  :options="currencyCodeOptions"
                  item="label"
                  :placeholder="$t('Select currency')"
                  readonly
                />
              </b-col>

              <!--  Reference Number -->
              <b-col md="4">
                <InputField
                  id="reference-number"
                  v-model="customer.referenceNumber"
                  :label="$t('Reference Number')"
                  :placeholder="$t('Enter reference number')"
                  type="text"
                  show-icon
                  :on-click="onViewInfo"
                />
              </b-col>
              <!--  IPPIS/Oracle ID -->
              <b-col md="4">
                <InputField
                  id="ippis-oracle"
                  v-model="customer.employeeID"
                  :label="$t('IPPIS/Oracle ID')"
                  :placeholder="$t('Enter IPPIS/Oracle ID')"
                  type="text"
                />
              </b-col>
              <!--  IPPIS/Oracle ID -->
            </b-row>
          </b-form>
        </validation-observer>

      </tab-content>

      <tab-content
        v-if="actionType !== 'add'"
        :title="$t('Profit/Fees')"
        :before-change="validateInterest"
      >
        <validation-observer ref="interestRules">
          <b-form>
            <b-row>
              <b-col md="4">
                <SwitchButton
                  v-model="customer.overrideProductApplicableInterest"
                  :label="$t('Override Product Applicable Profit')"
                  name="default-credit"
                  class="mt-2"
                  :readonly="isReadonly"
                />
              </b-col>
            </b-row>

            <div v-if="setDefaultApplicableInterest(customer.overrideProductApplicableInterest)">

              <b-row>
                <b-col md="6">
                  <SelectField
                    id="credit-interest"
                    v-model="customer.applyCreditInterest"
                    :label="$t('Apply Credit Profit')"
                    :reduce="option => option.value"
                    :options="applyOptions"
                    :placeholder="$t('Select')"
                    :error-name="$t('Credit Profit')"
                    :rules="customer.overrideProductApplicableInterest ? 'required' : ''"
                    item="label"
                    :set-selected="setApplyCreditInterest"
                    :readonly="isReadonly"
                  />
                </b-col>

                <!-- Conditional -->
                <b-col
                  v-if="isConditional"
                  md="6"
                >
                  <SelectField
                    id="conditional"
                    v-model="interestConditionBasis"
                    :label="$t('Conditional')"
                    :reduce="option => option.value"
                    :options="conditionalOptions"
                    :placeholder="$t('Select')"
                    :error-name="$t('Conditional')"
                    rules="required"
                    item="label"
                    :set-selected="setProductInterest"
                    :readonly="isReadonly"
                  />
                </b-col>

                <b-col
                  v-if="isCreditInterest"
                  md="6"
                >
                  <InputField
                    id="credit-interest-rate"
                    v-model.number="customer.interestRate"
                    :label="$t('Credit Profit Rate')"
                    type="number"
                    :error-name="$t('Credit Profit Rate')"
                    :rules="customer.overrideProductApplicableInterest ? 'required|max-value:100|positive' : ''"
                    :is-allow-negative-numbers="false"
                    onpaste="return false;"
                    :readonly="isReadonly"
                  />
                </b-col>
              </b-row>

              <div
                v-if="isConditional"
                class="mb-5"
              >
                <b-form
                  ref="form"
                  class="repeater-form"
                  @submit.prevent="repeatProductInterest"
                >

                  <!-- Row Loop -->
                  <b-row
                    v-for="(item, index) in customer.productInterests"
                    :id="index.toString()"
                    :key="index.toString()"
                    ref="productInterest"
                    class="mt-2 d-flex align-items-center"
                  >
                    <b-col md="3">
                      <!-- No of Withdrawals(times) -->
                      <InputNumberField
                        :id="isInterestNoOfWithdrawal ? 'number-of-withdrawal' : 'customer-balance'"
                        v-model.number="item.interestConditionValue"
                        :label="isInterestNoOfWithdrawal ? $t('No of Withdrawals') : $t('Customer Balance (Amount)')"
                        :error-name="isInterestNoOfWithdrawal ? $t('No of Withdrawal') : $t('Balance')"
                        :rules="customer.overrideProductApplicableInterest ? 'required' : ''"
                        :readonly="isReadonly"
                      />
                    </b-col>

                    <b-col md="3">
                      <!-- Credit Interest Rate -->
                      <InputNumberField
                        id="credit-interest-rate"
                        v-model.number="item.interestRate"
                        :label="$t('Rate')"
                        :rules="customer.overrideProductApplicableInterest ? 'required|max-value:100|positive' : ''"
                        :error-name="$t('Rate')"
                        :readonly="isReadonly"
                      />
                    </b-col>

                    <b-col md="2">
                      <!-- Remove Button -->
                      <feather-icon
                        v-if="actionType !== 'view'"
                        v-b-tooltip.hover.bottom="$t('Remove')"
                        icon="XIcon"
                        size="25"
                        class="mr-25 cursor-pointer text-danger"
                        @click="removeProductInterestItem(index)"
                      />

                    </b-col>
                  </b-row>

                </b-form>

                <Button
                  v-show="showAddProductInterest"
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  variant="primary"
                  class="mb-3"
                  show-icon
                  size="md"
                  icon="PlusIcon"
                  :label="$t('Add New Condition')"
                  @click="repeatProductInterest"
                />

              </div>
            </div>

            <b-row v-if="actionType !== 'add'">
              <b-col md="6">
                <SwitchButton
                  v-model="customer.overrideProductApplicableFee"
                  :label="$t('Override Product Applicable Fee')"
                  name="cam-fee"
                  class="mt-2"
                  :readonly="isReadonly"
                />
              </b-col>
            </b-row>
            <!-- <b-row v-if="actionType !== 'add'">
              <b-col md="6">
                <SwitchButton
                  v-model="customer.overrideProductApplicableFee"
                  :label="$t('Override Product Applicable Fee"
                  name="cot-fee"
                  class="mt-2"
                  :readonly="isReadonly"
                />
              </b-col>
            </b-row> -->

            <div v-if="setProductApplicableFee(customer.overrideProductApplicableFee)">
              <b-row>
                <b-col md="6">
                  <SelectField
                    id="apply-fee"
                    v-model="customer.applyFee"
                    :label="$t('Apply Fee')"
                    :options="feeOptions"
                    :placeholder="$t('Select')"
                    :reduce="option => option.value"
                    :error-name="$t('Apply Fee')"
                    :rules="customer.overrideProductApplicableInterest ? 'required' : ''"
                    item="label"
                    :set-selected="setApplyFee"
                    :readonly="isReadonly"
                  />
                </b-col>

                <!-- <b-col
                  v-if="isConditionalFee"
                  md="6"
                >
                  <SelectField
                    id="conditional"
                    v-model="feeConditionBasis"
                    :label="$t('Conditional"
                    :options="conditionalOptions"
                    :placeholder="$t('Select"
                    :error-name="$t('Conditional"
                    :rules="customer.overrideProductApplicableInterest ? 'required' : ''"
                    :reduce="option => option.value"
                    item="label"
                    :set-selected="setProductFee"
                    :readonly="isReadonly"
                  />
                </b-col> -->
              </b-row>

              <div
                v-if="isConditionalFee"
                class="mb-5"
              >
                <b-form
                  ref="productFeeForm"
                  class="repeater-form"
                  @submit.prevent="repeatProductFees"
                >

                  <!-- Row Loop -->
                  <b-row
                    v-for="(item, index) in customer.productFees"
                    :id="item.id"
                    :key="item.id"
                    ref="productFees"
                    class="align-items-center"
                  >
                    <b-col
                      md="3"
                    >
                      <!-- No of Withdrawals(times) -->
                      <InputNumberField
                        :id="isFeeNoOfWithdrawal ? 'number-of-withdrawal' : 'customer-balance'"
                        v-model.number="item.feeConditionValue"
                        :label="isFeeNoOfWithdrawal ? $t('No of Withdrawal') : $t('Customer Balance (Amount)')"
                        ::error-name="isFeeNoOfWithdrawal ? $t('No of Withdrawal') : $t('Customer Balance')"
                        :rules="customer.overrideProductApplicableInterest ? 'required' : ''"
                        :readonly="isReadonly"
                      />
                    </b-col>

                    <b-col md="3">
                      <!-- Flat Amount -->
                      <InputNumberField
                        id="flat-amt"
                        v-model.number="item.flatAmount"
                        :label="$t('Flat Amount')"
                        rules="required|positive"
                        :error-name="$t('Flat Amount')"
                        :readonly="isReadonly"
                      />
                    </b-col>
                    <b-col md="3">
                      <!-- Amount Per Transaction -->
                      <InputNumberField
                        id="amt-per-trans"
                        v-model.number="item.flatAmountPerTransaction"
                        :label="$t('Amount per Trans')"
                        :rules="customer.overrideProductApplicableInterest ? 'required|positive' : ''"
                        :error-name="$t('Amount per Trans')"
                        :readonly="isReadonly"
                      />
                    </b-col>
                    <b-col md="3">
                      <!-- Percentage on Turnover -->
                      <InputField
                        id="percentage-on-turnover"
                        v-model.number="item.percentageOnTurnover"
                        :label="$t('% on Turnover')"
                        type="number"
                        :rules="customer.overrideProductApplicableInterest ? 'required|max-value:100|positive' : ''"
                        :error-name="$t('% on Turnover')"
                        :readonly="isReadonly"
                      />
                    </b-col>

                    <b-col md="3">
                      <!-- Minimum Amount To Charge -->
                      <InputNumberField
                        id="min-amount-to-charge"
                        v-model.number="item.minimumAmount"
                        :label="$t('Minimum Amount')"
                        :rules="customer.overrideProductApplicableInterest ? 'required|amount:@maxFee|positive' : ''"
                        :error-name="$t('Minimum Amount')"
                        :readonly="isReadonly"
                      />
                    </b-col>

                    <b-col md="3">
                      <!-- Maximum Amount To Charge -->
                      <InputNumberField
                        id="maximum-amount-to-charge"
                        v-model.number="item.maximumAmount"
                        :label="$t('Maximum Amount')"
                        :rules="customer.overrideProductApplicableInterest ? 'required|positive' : ''"
                        vid="maxFee"
                        :error-name="$t('Maximum Amount')"
                        :readonly="isReadonly"
                      />
                    </b-col>

                    <b-col
                      md="2"
                    >
                      <!-- Remove Button -->

                      <feather-icon
                        v-if="actionType !== 'view'"
                        v-b-tooltip.hover.bottom="$t('Remove')"
                        icon="XIcon"
                        size="25"
                        class="mr-25 cursor-pointer text-danger"
                        @click="removeProductFeeItem(index)"
                      />

                    </b-col>

                  </b-row>

                </b-form>

                <b-button
                  v-show="showAddProductFee"
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  variant="primary"
                  @click="repeatProductFees"
                >
                  <feather-icon
                    icon="PlusIcon"
                    class="mr-25"
                  />
                  <span>{{ $t('Add New Condition') }}</span>
                </b-button>
              </div>
              <b-row v-if="isApplyFee">
                <!-- <b-col md="6">
                  <SelectField
                    id="fee"
                    v-model="feeID"
                    :reduce="option => option.value"
                    :label="$t('Fee"
                    :options="camFeeOptions"
                    :placeholder="$t('Select Fee"
                    :error-name="$t('Fee"
                    rules="required"
                    item="label"
                    :set-selected="setCOTFees"
                    :readonly="isReadonly"
                  />
                </b-col> -->

                <b-col md="6">
                  <!-- <InputField
                    id="flat-amount"
                    v-model="customer.flatAmount"
                    :label="$t('Flat Amount"
                    type="text"
                  /> -->
                  <InputNumberField
                    id="flat-amount"
                    v-model="customer.flatAmount"
                    :label="$t('Flat Amount')"
                    :placeholder="$t('Enter flat amount')"
                    type="number"
                    :error-name="$t('Flat Amount')"
                    :rules="customer.overrideProductApplicableInterest ? 'required|positive' : ''"
                  />
                </b-col>

                <b-col md="6">
                  <!-- <InputField
                    id="flat-amount-per-transaction"
                    v-model.number="customer.flatAmountPerTransaction"
                    :label="$t('Flat Amount Per Transaction"
                    type="text"
                  /> -->
                  <InputNumberField
                    id="flat-amount-per-transaction"
                    v-model="customer.flatAmountPerTransaction"
                    :label="$t('Flat Amount Per Transaction')"
                    :placeholder="$t('Enter flat amount per transaction')"
                    type="number"
                    :error-name="$t('Flat Amount Per Transaction')"
                    :rules="customer.overrideProductApplicableInterest ? 'required|positive' : ''"
                  />
                </b-col>

                <b-col md="6">
                  <InputNumberField
                    id="percentage-on-turnover"
                    v-model="customer.percentageOnTurnover"
                    :label="$t('Percentage on Turnover')"
                    :placeholder="$t('Enter percentage on turnover')"
                    type="number"
                    :error-name="$t('Percentage on Turnover')"
                    :rules="customer.overrideProductApplicableInterest ? 'required|max-value:100|positive' : ''"
                  />
                </b-col>

                <b-col md="6">
                  <InputNumberField
                    id="maximum-amount-to-charge"
                    v-model.number="customer.maximumAmount"
                    :label="$t('Maximum Amount to Charge')"
                    :placeholder="$t('Enter maximum amount to charge')"
                    type="number"
                    vid="maxFee"
                    :error-name="$t('Maximum Amount to Charge')"
                    :rules="customer.overrideProductApplicableInterest ? 'required|positive' : ''"
                    :is-allow-negative-numbers="false"
                    onpaste="return false;"
                  />
                </b-col>

                <b-col md="6">
                  <InputNumberField
                    id="minimum-amount-to-charge"
                    v-model.number="customer.minimumAmount"
                    :label="$t('Minimum Amount to Charge')"
                    :placeholder="$t('Enter minimum amount to charge')"
                    type="number"
                    :error-name="$t('Minimum Amount to Charge')"
                    :rules="customer.overrideProductApplicableInterest ? 'required|amount:@maxFee|positive' : ''"
                    :is-allow-negative-numbers="false"
                    onpaste="return false;"
                  />
                </b-col>
              </b-row>

            </div>

          </b-form>
        </validation-observer>

      </tab-content>

      <tab-content
        :title="$t('Preferences')"
        :before-change="validateStatement"
      >
        <validation-observer ref="statementRules">
          <b-form>
            <h6 class="section-label mb-2">
              {{ $t('Statement Delivery Preference') }}
            </h6>

            <b-row>
              <b-col md="6">
                <SwitchButton
                  v-model="overridePhoneNumber"
                  :label="$t('Override Notification Phone Number')"
                  name="override-notification-phone"
                  class="mt-2"
                  @change.native="value => onOverridePhoneNumberChange(value)"
                />
                <TextAreaField
                  id="sec-phone-number"
                  v-model="customer.notificationPhoneNumber"
                  :label="$t('Notification Phone Number')"
                  :placeholder="
                    $t('For multiple Phone Numbers, separate by comma (,)')
                  "
                  type="text"
                  for="sec-phone-number"
                  rows="2"
                  :readonly="customer.notificationPhoneNumber ? false : isPhoneReadonly"
                  show-icon
                  :on-click="onViewPhoneInfo"
                />
              </b-col>
              <b-col md="6">
                <SwitchButton
                  v-model="overrideEmailAddress"
                  :label="$t('Override Notification Email')"
                  name="override-notification-email"
                  class="mt-2"
                  @change.native="value => onOverrideEmailChange(value)"
                />
                <TextAreaField
                  id="sec-email"
                  v-model="customer.notificationEmail"
                  :label="$t('Notification Email')"
                  :placeholder="
                    $t('For multiple Emails, separate by comma (,)')
                  "
                  type="text"
                  rows="2"
                  :readonly="customer.notificationEmail ? false : isEmailReadonly"
                  show-icon
                  :on-click="onViewEmailInfo"
                />
              </b-col>
            </b-row>

            <b-row class="">
              <b-col md="4">
                <SelectField
                  id="delivery-mode"
                  v-model="customer.statementDeliveryMode"
                  :label="$t('Delivery Mode')"
                  required="true"
                  :reduce="option => option.value"
                  :error-name="$t('Delivery Mode')"
                  :options="deliveryModeOptions"
                  item="label"
                  :placeholder="$t('Select delivery mode')"
                />
              </b-col>

              <b-col md="4">
                <SelectField
                  id="frequency"
                  v-model="customer.statementDeliveryFrequency"
                  :label="$t('Frequency')"
                  required="true"
                  :reduce="option => option.value"
                  :error-name="$t('Frequency')"
                  :options="frequencyOptions"
                  item="label"
                  :placeholder="$t('Select frequency')"
                />
              </b-col>
            </b-row>

            <div class="mt-5 mb-3">
              <h6 class="section-label mb-2 mt-2 ">
                {{ $t('Transaction Notification Preference') }}
              </h6>

              <b-row class="">
                <b-col
                  md="4"
                  class="mt-1"
                >
                  <div class="d-flex justify-content-between align-items-center">
                    <h6 class="mt-1">
                      {{ $t('Notify via') }}
                    </h6>

                    <b-form-checkbox
                      v-model="customer.enableEmailNotification"
                      inline
                    >
                      {{ $t('E-Mail') }}
                    </b-form-checkbox>
                    <b-form-checkbox
                      v-model="customer.enableSMSNotification"
                      inline
                    >
                      {{ $t('SMS') }}
                    </b-form-checkbox>
                  </div>

                </b-col>
              </b-row>
            </div>
          </b-form>
        </validation-observer>
      </tab-content>

      <tab-content
        v-if="customerType.toLowerCase() !== 'individual'"
        :title="$t('Mandate Rule')"
      >
        <div>
          <h6 class="section-label">
            {{ $t('Group Members') }}
          </h6>

          <b-row>
            <b-col
              cols="12"
              class="mb-2 mt-2"
            >
              <Button
                :label="$t('View Group Members')"
                size="sm"
                class="mb-2"
                @click="onViewGroupMembers"
              />
            </b-col>
          </b-row>
        </div>
        <b-form>
          <b-row>
            <b-col md="12">
              <h6 class="section-label mb-2 mt-2">
                {{ $t('Signatory Information') }}
              </h6>
              <validation-observer ref="signatoryRules">
                <b-form @submit.prevent="createSignatoryAction">
                  <b-row>
                    <b-col md="5">
                      <SelectField
                        id="signatory"
                        v-model="signatory"
                        :label="$t('Signatory')"
                        :rules="signatoryRule"
                        :error-name="$t('Signatory')"
                        :reduce="option => option"
                        :options="signatoryOptions"
                        item="label"
                        :placeholder="$t('Select')"
                      />
                    </b-col>
                    <b-col md="5">
                      <SelectField
                        id="category"
                        v-model="category"
                        :label="$t('Category')"
                        :rules="signatoryRule"
                        :error-name="$t('Category')"
                        :reduce="option => option"
                        :options="categoryOptions"
                        item="label"
                        :placeholder="$t('Select')"
                      />
                    </b-col>
                    <b-col
                      md="2"
                      class="mt-2"
                    >
                      <Button
                        :label="$t('Add')"
                        size="md"
                        @click="createSignatoryAction"
                      />
                    </b-col>
                  </b-row>
                </b-form>
              </validation-observer>

              <div
                v-if="customerType.toLowerCase() !== 'individual' && signatoryList.length"
                class="mt-2"
              >
                <b-table
                  ref="signatoryListTable"
                  select-mode="single"
                  :items="signatoryList"
                  responsive
                  :fields="translatedSignatoryListHeaders"
                  primary-key="id"
                  selectable
                  show-empty
                  :empty-text="$t('No signatory added')"
                  :sort-by.sync="sortBy"
                  :sort-desc.sync="sortDesc"
                  class="position-relative"
                >
                  <template #cell(SN)="data">
                    {{ data.index + 1 }}
                  </template>
                  <template #cell(actions)="data">
                    <Button
                      :label="$t('Remove')"
                      size="sm"
                      @click="removeSignatoryAction(data.item)"
                    />
                  </template>
                </b-table>
              </div>

            </b-col>
          </b-row>

          <b-row
            v-if="mandate.ruleConfig[0].categoryConfig.length"
            class=""
          >
            <b-col md="12">
              <h6 class="section-label mb-2 mt-2">
                {{ $t('Rule Configuration') }}
              </h6>

              <b-form @submit.prevent="createRuleInfoAction">
                <validation-observer
                  ref="ruleConfigurationRules"
                >

                  <b-row
                    v-for="(item) in mandate.ruleConfig[0].categoryConfig"
                    :id="item.category"
                    :key="item.category"
                  >
                    <b-col md="6">
                      <InputField
                        id="category"
                        v-model="item.category"
                        :label="$t('Category')"
                        type="text"
                        readonly
                      />
                    </b-col>

                    <b-col md="6">
                      <InputField
                        id="customer-count"
                        v-model.number="item.authorizableNumber"
                        :label="$t('Customer Count')"
                        rules="required|integer|positive"
                        :placeholder="$t('Enter customer count')"
                        :error-name="$t('Customer Count is required')"
                        type="number"
                      />
                    </b-col>
                  </b-row>

                  <b-row v-if="mandate.ruleConfig[0].categoryConfig.length">
                    <b-col md="6">
                      <SelectField
                        id="limit"
                        v-model="mandate.ruleConfig[0].isLimited"
                        :label="$t('Limit')"
                        rules="required"
                        :error-name="$t('Limit')"
                        :reduce="option => option.value"
                        :options="limitOptions"
                        item="label"
                        :placeholder="$t('Select')"
                        :set-selected="setAmountLimit"
                      />
                    </b-col>

                    <b-col
                      v-if="setAmountLimit(mandate.ruleConfig[0].isLimited)"
                      md="6"
                    >
                      <InputNumberField
                        id="amount"
                        v-model.number="mandate.ruleConfig[0].amount"
                        :label="$t('Amount')"
                        :placeholder="$t('Enter amount')"
                        rules="required|integer|positive"
                        :error-name="$t('Amount')"
                      />
                    </b-col>
                  </b-row>

                  <div class="mt-2 mb-2">
                    <Button
                      v-if="mandate.ruleConfig[0].categoryConfig.length"
                      :disabled="isAddRuleDisabled"
                      :loading="isAddRuleLoading"
                      :label="$t('Add Rule')"
                      size="md"
                      @click="createRuleInfoAction"
                    />
                  </div>

                </validation-observer>
              </b-form>
            </b-col>
          </b-row>

          <b-row
            v-if="mandateList.length"
            class=""
          >
            <b-col md="12">
              <h6 class="section-label mb-2 mt-2">
                {{ $t('Mandate Information') }}
              </h6>
              <div>
                <b-table
                  ref="mandateListTable"
                  select-mode="single"
                  :items="mandateList"
                  responsive
                  :fields="translatedMandateListHeaders"
                  primary-key="id"
                  selectable
                  show-empty
                  :empty-text="$t('No mandate added')"
                  :sort-by.sync="sortBy"
                  :sort-desc.sync="sortDesc"
                  class="position-relative"
                >
                  <template #cell(SN)="data">
                    {{ data.index + 1 }}
                  </template>
                  <template #cell(isLimited)="data">
                    {{ data.value === false ? $t('Unlimited') : $t('Limited') }}
                  </template>
                  <template #cell(amount)="data">
                    {{ data.value === 0 ? $t('Unlimited') : toAmount(data.value) }}
                  </template>
                  <template #cell(actions)="data">
                    <Button
                      :label="$t('Remove')"
                      size="sm"
                      @click="removeMandateAction(data.item)"
                    />
                  </template>
                </b-table>

              </div>

            </b-col>
          </b-row>
        </b-form>

      </tab-content>

      <tab-content :title="$t('Referee')">
        <h6 class="section-label mb-2">
          {{ $t('Referee Information') }}
        </h6>
        <b-row
          v-for="(item, index) in refereeList"
          :id="item.id"
          :key="index"
          class="mt-2"
        >
          <b-col
            md="3"
          >
            <InputField
              id="category"
              v-model="item.customerName"
              :label="`${$t('Referee')} ${index + 1}`"
              type="text"
              readonly
            />
          </b-col>

          <b-col
            md="2"
            class="mt-2"
          >
            <!-- Remove Button -->
            <feather-icon
              v-b-tooltip.hover.bottom="'Remove'"
              icon="XIcon"
              class="mr-25 cursor-pointer text-danger"
              @click="removeRefereeItem(index)"
            />
          </b-col>
        </b-row>

        <Button
          :label="$t('Add Referee')"
          :disabled="refereeLimit"
          size="md"
          class="mb-4"
          @click="showModal('referee-modal')"
        />

        <h6 class="section-label mb-2">
          {{ $t('Account Introducer') }}
        </h6>
        <b-row>
          <b-col md="6">
            <SelectField
              id="account-introducer-type"
              v-model="customer.introducerType"
              :label="$t('Account Introducer Type')"
              required="true"
              :error-name="$t('Account Introducer Type')"
              :options="translatedAccountIntroducerTypeOptions"
              item="label"
              :reduce="option => option.value"
              :placeholder="$t('Select account introducer type')"
              rules="required"
              :set-selected="setIntroducerType"
            />
          </b-col>
          <b-col
            v-if="customer.introducerType === 'BankStaff'"
            md="6"
          >
            <SelectField
              id="account-introducer"
              v-model="customer.introducerID"
              :label="$t('Account Introducer')"
              required="true"
              :error-name="$t('Account Introducer')"
              :options="accountStaffOptions"
              item="label"
              :reduce="option => option.value"
              :placeholder="$t('Select account introducer')"
              rules="required"
              :set-selected="fetchStaffName"
            />
          </b-col>
          <b-col
            v-else
            md="6"
          >
            <b-row
              v-for="(item, index) in customerIntroducerList"
              :id="item.id"
              :key="index"
            >
              <!-- {{ item.customerID }} -->
              <b-col
                md="10"
              >
                <InputField
                  id="category"
                  v-model="item.customerName"
                  :label="$t('Account Introducer')"
                  type="text"
                  readonly
                />
              </b-col>

              <b-col
                md="2"
                class="mt-2"
              >

                <feather-icon
                  v-b-tooltip.hover.bottom="$t('Remove')"
                  icon="XIcon"
                  class="mr-25 cursor-pointer text-danger"
                  @click="removeCustomerItem(index)"
                />
              </b-col>
            </b-row>
            <Button
              v-if="customer.introducerType"
              :label="$t('Search Customer')"
              :disabled="serachCustomerLimit"
              size="md"
              class="mt-2"
              @click="showModal('customer-modal')"
            />
          </b-col>
        </b-row>

      </tab-content>
      <tab-content
        v-if="actionType === 'edit'"
        :title="$t('Group Connections')"
      >
        <h6 class="section-label mb-2">
          {{ $t('Group Connections') }}
        </h6>

        <b-row>
          <b-col md="6">
            <InputField
              id="group-connection-id"
              v-model="customer.groupConnectionID"
              :label="$t('Group Connection ID')"
              :placeholder="$t('Enter group connection ID')"
              type="text"
              readonly
            />
          </b-col>
          <b-col md="6">
            <InputField
              id="group-connection-name"
              v-model="customer.groupConnectionName"
              :label="$t('Group Connection Name')"
              :placeholder="$t('Enter group connection Name')"
              type="text"
              readonly
            />
          </b-col>
        </b-row>
      </tab-content>

      <!-- Other Documents tab-->
      <tab-content
        :title="$t('Other Documents')"
        :before-change="validatUploads"
      >
        <validation-observer ref="uploadRules">
          <b-form>
            <div>
              <div class="w-full mt-4 p-10">
                <div class="d-flex mb-0 mt-2 align-items-center justify-content-between">
                  <h4>Other Documents Upload</h4>

                  <b-button
                    v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                    variant="primary"
                    class="ml-4"
                    @click="addNewDocument()"
                  >
                    <feather-icon
                      icon="PlusIcon"
                      class="mr-25"
                    />
                    <span>{{ $t('Add New Document') }}</span>
                  </b-button>
                </div>
                <b-row
                  v-for="(item, index) in documentsFormData"
                  :id="index"
                  :key="index"
                  class="mb-2"
                >
                  <b-col md="6">
                    <SelectField
                      id="document-type"
                      v-model="item.type"
                      :reduce="option => option.value"
                      :label="$t('Document Type')"
                      :placeholder="$t('Select')"
                      :options="translatedDocumentTypeOptions"
                      :error-name="$t('Document Type')"
                      rules="required"
                      item="label"
                    />
                  </b-col>
                  <b-col
                    md="7"
                  >
                    <label>{{ $t('Upload Documents') }}</label>

                    <div class="upload-image">
                      <Spinner :show="documentLoadSpinner" />
                      <b-form-group>
                        <b-form-file
                          :placeholder="$t('Choose a file or drop it here...')"
                          drop-:placeholder="$t('Drop file here...')"
                          accept="image/jpeg, image/png, image/jpg, application/pdf, .doc, .docx, application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                          plain
                          @change="onDocumentsUpload(index, $event)"
                        />
                        <div
                          v-if="!item.url"
                          class="upload-instruction"
                        >
                          <h4>{{ $t('Upload Document File') }}</h4>
                          <p>{{ $t('Drag & drop file or choose from your device to upload') }}</p>
                          <p class="format">
                            {{ $t('Format accepted') }} (jpg, png, jpeg, docx, pdf)
                          </p>
                        </div>
                        <div v-else>
                          <div v-if="item.fileExtension === 'png'|| item.fileExtension === 'jpeg'|| item.fileExtension === 'jpg'">
                            <b-img
                              :src="item.url"
                              block
                              rounded
                              width="250px"
                              height="250px"
                            />
                          </div>
                          <div
                            v-if="item.fileExtension === 'pdf'"
                          >
                            <b-img
                              :src="pdfIcon"
                              block
                              rounded
                              width="250px"
                              height="250px"
                            />
                          </div>
                          <div
                            v-else-if="item.fileExtension === 'vnd'"
                          >
                            <b-img
                              :src="wordIcon"
                              block
                              rounded
                              width="250px"
                              height="250px"
                            />
                          </div>
                          <div
                            v-else
                          >
                            <b-img
                              src=""
                            />
                          </div>
                          <div
                            v-if="item.url"
                            class="trash"
                          >
                            <feather-icon
                              icon="Trash2Icon"
                              size="20"
                              class="text-primary"
                              @click="deleteDocumentFile(item.id, index)"
                            />
                          </div>
                          <div
                            v-if="item.url"
                            class="view-details"
                          >
                            <feather-icon
                              icon="EyeIcon"
                              size="20"
                              class="text-primary"
                              @click="viewDocumentFile(item.url, item.fileName, item.fileExtension)"
                            />
                          </div>
                          <!-- </div> -->
                        </div>
                      </b-form-group>
                    </div>
                  </b-col>
                  <b-col
                    md="3"
                    class="mt-0"
                  >
                    <b-row>
                      <!-- Remove Button -->
                      <b-col
                        v-if="item.url"
                        md="12"
                        class="mt-1"
                      >
                        <b-button
                          v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                          variant="primary"
                          class="ml-0"
                          @click="removeDocument(item.id, index)"
                        >
                          <feather-icon
                            icon="XIcon"
                            class="mr-25"
                          />
                          <span>{{ $t('Remove') }}</span>
                        </b-button>
                      </b-col>
                      <b-col
                        v-if="item.url"
                        md="12"
                        class="mt-1"
                      >
                        <b-button
                          v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                          variant="primary"
                          class="ml-0"
                          @click="handleDownload(item.url)"
                        >
                          <feather-icon
                            icon="DownloadIcon"
                            class="mr-25"
                          />
                          <span>{{ $t('Download') }}</span>
                        </b-button>
                      </b-col>
                    </b-row>
                  </b-col>
                </b-row>
              </div>
            </div>
          </b-form>
        </validation-observer>
      </tab-content>

      <template
        slot="finish"
      >
        <div class="wizard-footer-right">
          <Button
            v-if="actionType !== 'view'"
            :label="$t('Submit')"
            :loading="isLoading"
            :disabled="isDisabled"
            size="lg"
            @click="handleSubmit"
          />
        </div>

      </template>
    </form-wizard>

    <b-modal
      id="search-modal"
      ref="search-modal"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      :title="$t('Customer Account Details')"
    >

      <b-table
        ref="customerListTable"
        select-mode="single"
        :items="customerList"
        :per-page="pageSize"
        :current-page="currentPage"
        responsive
        :fields="translatedTableHeaders"
        primary-key="id"
        selectable
        show-empty
        :empty-text="$t('No result found')"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        class="position-relative"
        @row-selected="onSelectCustomer"
      >
        <template #cell(SN)="data">
          {{ getOverallIndex(data.index) }}
        </template>
      </b-table>

      <TablePagination
        v-model="currentPage"
        :from="from"
        :to="to"
        :total-rows="totalEntries"
        :per-page="perPage"
      />

      <template
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>

    <!-- Referee modal -->
    <b-modal
      id="referee-modal"
      ref="refereeModal"
      :ok-title="$t('Search')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      no-close-on-backdrop
      centered
      size="md"
      :title="$t('Search Referee')"
      modal-footer
      @ok="handleRefereeSearch"
      @cancel="resetRefereeModal"
      @hidden="resetRefereeModal"
    >
      <validation-observer ref="searchRules">
        <b-form @submit.prevent="handleSearch">
          <b-row class="match-height">
            <Alert
              :show="showFormErrorAlert"
              :message="$t('Customer\'s account name or account number or customer id is required')"
            />
            <b-col md="12">
              <InputField
                id="customer-id"
                v-model="refereeSearchFormData.customerID"
                :label="$t('Customer ID')"
                :placeholder="$t('Enter customer id')"
                type="text"
              />
            </b-col>

            <b-col md="12">
              <InputField
                id="customer-name"
                v-model="refereeSearchFormData.name"
                :label="$t('Customer\'s Name')"
                :placeholder="$t('Enter customer\'s name')"
                type="text"
                :error-name="$t('Customer\'s Name')"
                rules=""
              />
            </b-col>
            <b-col md="12">
              <InputField
                id="email-address"
                v-model="refereeSearchFormData.email"
                :label="$t('Customer\'s Email')"
                :placeholder="$t('Enter email address')"
                type="email"
              />
            </b-col>

          </b-row>
        </b-form>
      </validation-observer>
    </b-modal>

    <!-- Customer modal -->
    <b-modal
      id="customer-modal"
      ref="customerModal"
      :ok-title="$t('Search')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      no-close-on-backdrop
      centered
      size="md"
      :title="$t('Search Customer')"
      modal-footer
      @ok="handleCustomerSearch"
      @cancel="resetRefereeModal"
      @hidden="resetRefereeModal"
    >
      <validation-observer ref="searchRules">
        <b-form @submit.prevent="handleSearch">
          <b-row class="match-height">
            <Alert
              :show="showFormErrorAlert"
              :message="$t('Customer\'s account name or account number or customer id is required')"
            />
            <b-col md="12">
              <InputField
                id="customer-id"
                v-model="refereeSearchFormData.customerID"
                :label="$t('Customer ID')"
                :placeholder="$t('Enter customer id')"
                type="text"
              />
            </b-col>

            <b-col md="12">
              <InputField
                id="customer-name"
                v-model="refereeSearchFormData.name"
                :label="$t('Customer\'s Name')"
                :placeholder="$t('Enter customer\'s name')"
                type="text"
                :error-name="$t('Customer\'s Name')"
                rules=""
              />
            </b-col>
            <b-col md="12">
              <InputField
                id="email-address"
                v-model="refereeSearchFormData.email"
                :label="$t('Customer\'s Email')"
                :placeholder="$t('Enter email address')"
                type="email"
              />
            </b-col>

          </b-row>
        </b-form>
      </validation-observer>
    </b-modal>

    <b-modal
      id="signatory-mandate-table"
      ref="signatoryMandateTable"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      :title="$t('Group Members')"
    >

      <div class="m-2">

        <!-- Table Top -->
        <b-row>

          <!-- Per Page -->
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >

            <!-- <TableEntries
              v-model="perPage"
              :options="perPageOptions"
            /> -->

          </b-col>

        </b-row>

      </div>

      <b-table
        ref="signatoryMandateTable"
        select-mode="single"
        :items="signatoryMandateList"
        responsive
        :fields="translatedSignatoryMandateTableHeaders"
        :per-page="perPage"
        :current-page="currentPage"
        primary-key="id"
        show-empty
        :empty-text="$t('No result found')"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        class="position-relative mt-2"
      >
        <template #cell(SN)="data">
          {{ data.index + 1 }}
        </template>

        <template #cell(passport)="data">
          <b-media vertical-align="center">
            <template #aside>
              <b-avatar
                size="32"
                :src="data.item.passport"
                button
                @click="onViewPassport(data.item.passport, data.item.fullName)"
              />
            </template>
          </b-media>
        </template>

        <template #cell(signature)="data">
          <b-media vertical-align="center">
            <template #aside>
              <b-avatar
                size="32"
                :src="data.item.signature"
                button
                @click="onViewSignature(data.item.signature, data.item.fullName)"
              />
            </template>
          </b-media>
        </template>
      </b-table>

      <!-- <TablePagination
        v-model="signatoryCurrentPage"
        :from="signatoriesFrom"
        :to="signatoriesTo"
        :total-rows="totalSignatories"
        :per-page="signatoriesPerPage"
      /> -->

      <template
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>

    <b-modal
      id="avatar-modal"
      ref="avatarModal"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      :title="avatarModalTitle"
      @hidden="resetAvatarModal"
      @cancel="resetAvatarModal"
    >

      <div class="m-2 text-center">

        <b-row>
          <b-col cols="12">

            <b-avatar
              size="30rem"
              :src="avatarLg"
            />
          </b-col>

        </b-row>

      </div>

      <template
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- Filter modal -->

    <b-modal
      id="info-modal"
      ref="infoModal"
      centered
      size="md"
      ok-only
      :ok-title="$t('Okay')"
      @ok="hideModal('info-modal')"
    >
      <div class="p-2">
        <p>{{ $t('External Customer Account Reference') }} </p>
      </div>
    </b-modal>
    <b-modal
      id="view-avatar-modal"
      ref="avatarModal"
      no-close-on-backdrop
      :ok-title="$t('Download File')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      :title="viewDocumentModalTitle"
      @hidden="resetDocumentModal"
      @cancel="resetDocumentModal"
      @ok="handleDownload(viewDocumentData)"
    >
      <div class="m-2 text-center">
        <b-row>
          <b-col cols="12">
            <div v-if="fileExtension === 'png'|| fileExtension === 'jpeg'|| fileExtension === 'jpg'">
              <b-img
                :src="viewDocumentData"
                block
                fluid
                rounded
              />
            </div>
            <div v-else-if="fileExtension === 'pdf'">
              <b-img
                :src="pdfIcon"
                block
                width="250px"
                height="250px"
                rounded
              />
            </div>
            <div v-else-if="fileExtension === 'vnd'">
              <b-img
                :src="wordIcon"
                block
                width="250px"
                height="250px"
                rounded
              />
            </div>
            <div v-else>
              <b-img
                src=""
              />
            </div>
          </b-col>
        </b-row>
      </div>
    </b-modal>
    <b-modal
      id="info-email-modal"
      ref="infoEmailModal"
      centered
      size="md"
      ok-only
      :ok-title="$t('Okay')"
      @ok="hideModal('info-email-modal')"
    >
      <div class="p-2">
        <p>{{ $t('For multiple Email Addresses, separate by comma') }} (,)</p>
      </div>
    </b-modal>
    <b-modal
      id="info-phone-modal"
      ref="infoPhoneModal"
      centered
      size="md"
      ok-only
      :ok-title="$t('Okay')"
      @ok="hideModal('info-phone-modal')"
    >
      <div class="p-2">
        <p>{{ $t('For multiple Phone Numbers, separate by comma') }} (,)</p>
      </div>
    </b-modal>
    <FullScreenSpinner :show="showSpinner" />
  </div>
</template>

<script>
import { FormWizard, TabContent } from 'vue-form-wizard'
import 'vue-form-wizard/dist/vue-form-wizard.min.css'
import {
  BFormCheckbox, BRow, BCol, BTable, BForm, VBTooltip, BMedia, BAvatar, BButton, BCard,
  BFormGroup, BFormFile, BImg,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { heightFade } from '@core/directives/animations'
import {
  required, positive, number, amount,
} from '@validations'
import { ValidationObserver } from 'vee-validate'
import { permissionMixin } from '@core/mixins/permission'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { mapGetters, mapActions } from 'vuex'
import { heightTransition } from '@core/mixins/ui/transition'
import { toastAlert } from '@core/mixins/toast'
// import { v4 as uuidv4 } from 'uuid'
import Spinner from '@/views/components/spinner/Spinner.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import TablePagination from '@/views/components/table/TablePagination.vue'
import getStatusColor from '@/helpers/getStatusColor'
import convertZeroToString from '@/helpers/convertZeroToString'
import { transformMandateRule, transformMandateRuleConfig } from '@/helpers/transformers'
import {
  toAmount, formatAmount, toBalance, toKobo,
} from '@/helpers/formatAmount'
// import TableEntries from '@/views/components/table/TableEntries.vue'
import InputField from '../../../../forms/InputField.vue'
import SelectField from '../../../../forms/SelectField.vue'
import TextAreaField from '../../../../forms/TextAreaField.vue'
import InputNumberField from '../../../../forms/InputNumberField.vue'
import {
  accountStatus, accountTier, accessLevel, deliveryMode, category, frequency, communication, categoryOfAccountOptions, sectorCodeOptions,
  applyInterest, conditionalOptions, camChargeCycle, transactionType, pdfIcon, wordIcon,
  gender, titleOptions, countries, religionOptions, meansOfIdOptions, maritalStatusOptions, relationshipOptions,
} from '@/helpers/data'
import Button from '../../../../components/button/Button.vue'
import Alert from '../../../../components/alert/Alert.vue'
import { toYearWithDate, toDateWithYear } from '../../../../../helpers/formatDate'
import { accountPermissions } from '../../../../../helpers/permissions'
import SwitchButton from '../../../../components/switch/Switch.vue'
import DatePickerField from '../../../../forms/DatePickerField.vue'

export default {
  components: {
    BRow,
    BCol,
    BForm,
    InputField,
    SelectField,
    SwitchButton,
    FormWizard,
    TabContent,
    Alert,
    BMedia,
    BAvatar,
    BCard,
    BButton,
    BTable,
    BFormCheckbox,
    Spinner,
    FullScreenSpinner,
    ValidationObserver,
    TablePagination,
    Button,
    InputNumberField,
    TextAreaField,
    DatePickerField,
    BFormGroup,
    BFormFile,
    BImg,
  },
  directives: {
    Ripple,
    'height-fade': heightFade,
    'b-tooltip': VBTooltip,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, heightTransition, permissionMixin, toastAlert],
  data() {
    return {
      isEmailReadonly: true,
      overrideEmailAddress: false,
      isPhoneReadonly: true,
      overridePhoneNumber: false,
      permission: '',
      customerType: '',
      isEditable: false,
      isLoading: false,
      isDisabled: false,
      actionType: 'add',
      showEditable: '',
      avatarLg: '',
      conditionalOptions,
      avatarModalTitle: '',
      isAddRuleDisabled: false,
      isAddRuleLoading: false,
      showFormErrorAlert: false,
      showAmountLimit: false,
      showSearchButton: false,
      showBackButton: false,
      showCustomerInfo: false,
      showAddProductFee: false,
      showVirtualAccountWizard: false,
      searchType: '',
      customerList: [],
      refereeOption: [],
      required,
      positive,
      amount,
      number,
      toAmount,
      toBalance,
      showSpinner: false,
      sortDesc: false,
      feeID: null,
      interestID: null,
      selected: [],
      branchOptions: [],
      camFeeOptions: [],
      sortBy: 'accountName',
      filteredResult: [],
      applyOptions: applyInterest,
      chargeCycleOptions: camChargeCycle,
      transactionTypeOptions: transactionType,
      getStatusColor,
      searchParams: {
        customerID: '',
        name: '',
        email: '',
      },
      interestRate: null,
      virtualAccountFormData: {
        id: '',
        customerID: '',
        accountName: '',
        parentAccountNumber: '',
        accountType: '',
        virtualAccountName: '',
        branchCode: '',
        productCode: '',
        accountOfficerID: '',
        accountTierLevel: '',
        nin: '',
        alias: '',

        title: '',
        lastName: '',
        firstName: '',
        otherNames: '',
        address: '',
        gender: '',
        maritalStatus: '',
        maidenName: '',
        religion: '',
        customerId: '',
        phoneNumber: '',
        notificationPhoneNumber: '',
        email: '',
        notificationEmail: '',
        hometown: '',
        dateOfBirth: '',
        nationality: 'Nigeria',
        state: '',
        lga: '',
        bvn: '',
        identificationType: '',
        identificationNumber: '',
        pep: '',
        tin: '',
        oldCustomerID: '',
        employeeID: '',
      },
      accountypeOptions: [
        { label: 'Savings', value: 'Savings' },
        { label: 'Current', value: 'Current' },
      ],
      virtualAccountTypeOption: [
        { label: 'Virtual Proxy', value: 'VirtualProxy' },
        { label: 'Virtual Wallet', value: 'VirtualWallet' },
      ],
      genderOptions: gender,
      religionOptions,
      meansOfIdOptions,
      maritalStatusOptions,
      pepOptions: [
        { label: 'Yes', value: true },
        { label: 'No', value: false },
      ],
      relationshipOptions,
      datePickerConfig: {
        dateFormat: 'd/m/Y',
        ariaDateFormat: 'd/m/Y',
      },
      typeOfRelationship: '',
      countryCode: '',
      lgaOptions: [],
      countryOptions: ['Nigeria'],
      bvnInformationDetails: {},

      customer: {
        id: '',
        customerID: '',
        accountName: '',
        accountType: '',
        branchCode: '',
        branchName: '',
        productCode: '',
        productName: '',
        accountStatus: null,
        accountOfficerID: '',
        accountOfficerName: '',
        accountTierLevel: '',
        accessLevel: '',
        sectorCode: '',
        categoryOfAccount: '',
        interestCode: '',
        applyFee: '',
        applyCreditInterest: '',
        referenceNumber: '',
        notificationPhoneNumber: '',
        notificationEmail: '',
        overrideProductApplicableFee: false,
        applicableFee: 0,
        overrideProductApplicableInterest: false,
        applicableInterest: 0,

        signatories: {},
        statementDeliveryMode: '',
        statementDeliveryFrequency: '',
        enableEmailNotification: false,
        enableSMSNotification: false,
        refereeInformation: {},
        minimumBalanceRequired: 0,
        accountCreationChannel: null,
        mandateRuleConfig: {
          ruleConfig: [],
        },
        productFees: [],
        productInterests: [],
        flatAmount: null,
        flatAmountPerTransaction: null,
        percentageOnTurnover: null,
        minimumAmount: null,
        maximumAmount: null,
        chargeCycle: null,
        transactionType: '',
        interestRate: null,
        introducerType: '',
        introducerName: '',
        introducerID: '',
        employeeID: '',
        currencyCode: '',
        groupConnectionID: '',
        groupConnectionName: '',
      },
      mandate: {
        ruleConfig: [
          {
            categoryConfig: [],
            isLimited: true,
            amount: 0,
          },
        ],
      },
      signatoryMandateList: [],
      limit: '',
      signatory: '',
      category: '',
      feeConditionBasis: '',
      signatoryRule: 'required',
      customerTypeOptions: ['Individual', 'Group'],
      limitOptions: [{ label: 'Unlimited', value: false }, { label: 'Limited', value: true }],
      categoryOptions: category,
      signatoryList: [],
      mandateList: [],
      refereeList: [],
      customerIntroducerList: [],
      refereeSearchFormData: {
        customerID: '',
        name: '',
        email: '',
      },
      signatoryListHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'signatory', sortable: true, label: 'Signatory' },
        { key: 'category', sortable: true, label: 'Category' },
        { key: 'actions', label: 'Actions' },
      ],
      signatoryMandateTableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'fullName', sortable: true, label: 'Full Name' },
        { key: 'passport', sortable: true, label: 'Passport' },
        { key: 'signature', sortable: true, label: 'Signature' },
      ],
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'customerID', sortable: true, label: 'Customer ID' },
        { key: 'customerName', sortable: true, label: 'Customer Name' },
        { key: 'phoneNumber', sortable: true, label: 'Phone Number' },
        { key: 'email', sortable: true, label: 'Email' },
      ],
      mandateListHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'rule', sortable: true, label: 'Rule' },
        { key: 'isLimited', sortable: true, label: 'Limit' },
        { key: 'amount', sortable: true, label: 'Amount' },
        { key: 'actions', label: 'Actions' },
      ],
      timeout: null,
      isApplyFee: false,
      isConditional: false,
      isConditionalFee: false,
      isCreditInterest: false,
      isInterestNoOfWithdrawal: false,
      isFeeNoOfWithdrawal: false,
      showAddProductInterest: false,
      interestConditionBasis: '',

      signatoryOptions: [],
      signatories: [],
      selectedSignatories: [],
      groupMembers: [],
      notificationType: communication,
      productTypes: [],
      productTypeOptions: [],
      accountOfficerOptions: [],
      accountStaffOptions: [],
      accountIntroducerTypeOptions: [
        { label: 'Bank Staff', value: 'BankStaff' },
        { label: 'Customer', value: 'Customer' },
      ],
      accountStatusOption: accountStatus,
      totalSignatoriesCount: 0,
      signatoryCurrentPage: 1,
      signatoriesPerPage: 10,
      feeDetails: [],
      interestDetails: [],
      documentsFormData: [{
        type: '',
        url: '',
        file: '',
      }],
      viewDocumentData: '',
      viewDocumentModalTitle: '',
      fileExtension: '',
      documentLoadSpinner: false,
      documentTypeOptions: [
        { value: 1, label: 'Signature Specimen' },
        { value: 2, label: 'Proof Of Address' },
        { value: 3, label: 'Driver’s License' },
        { value: 4, label: 'National Identity Card' },
        { value: 5, label: 'Voters Card' },
        { value: 6, label: 'National Identity Number' },
        { value: 7, label: 'International Passport' },
        { value: 8, label: 'Passport Photograph' },
        { value: 9, label: 'Reference Letter' },
        { value: 10, label: 'Guarantor’s Letter' },
        { value: 11, label: 'Loan Application Form' },
        { value: 12, label: 'Credit Bureau Form' },
        { value: 13, label: 'Others' },
      ],
      documentIdentifier: '',
      pdfIcon,
      wordIcon,
      currencyCodeOptions: [],
    }
  },
  computed: {
    ...mapGetters({
      userDetails: 'auth/getCurrentUser',
    }),
    stateOptions() {
      const country = countries.find(item => item.name === 'Nigeria')
      return country.states.map(item => item.state).sort()
    },
    refereeLimit() {
      if (this.refereeList.length > 1) {
        return true
      }
      return false
    },
    serachCustomerLimit() {
      if (this.customerIntroducerList.length >= 1) {
        return true
      }
      return false
    },
    totalSignatories() {
      return this.totalSignatoriesCount
    },
    numberOfSignatories() {
      return Math.floor((this.totalSignatoriesCount + this.signatoriesPerPage - 1) / this.perPage)
    },
    signatoriesFrom() {
      return (this.signatoryCurrentPage * this.signatoriesPerPage) - (this.signatoriesPerPage - 1)
    },
    signatoriesTo() {
      return Math.min(this.signatoriesFrom + this.signatoriesPerPage - 1, this.totalSignatoriesCount)
    },
    accountStatusOptions() {
      return this.accountStatusOption.filter(item => item.label !== 'Active' && item.label !== 'Inactive' && item.label !== 'Dormant')
    },
    feeOptions() {
      return this.applyOptions.filter(item => item.value !== 'Conditional')
    },
    titleOptions() {
      return titleOptions.map(item => ({ label: this.$t(item.label), value: item.value }))
    },
    accountTypeOptions() {
      return this.accountypeOptions.map(item => ({ label: this.$t(item.label), value: item.value }))
    },
    virtualAccountsTypeOption() {
      return this.virtualAccountTypeOption.map(item => ({ label: this.$t(item.label), value: item.value }))
    },
    pepValueOptions() {
      return this.pepOptions.map(item => ({ label: this.$t(item.label), value: item.value }))
    },
    translatedDocumentTypeOptions() {
      return this.documentTypeOptions.map(item => ({ label: this.$t(item.label), value: item.value }))
    },
    accountTierOptions() {
      return accountTier.map(item => ({ label: this.$t(item.label), value: item.value }))
    },
    accessLevelOptions() {
      return accessLevel.map(item => ({ label: this.$t(item.label), value: item.value }))
    },
    sectorCodeOptions() {
      return sectorCodeOptions.map(item => ({ label: this.$t(item.label), value: item.value }))
    },
    categoryOfAccountOptions() {
      return categoryOfAccountOptions.map(item => ({ label: this.$t(item.label), value: item.value }))
    },
    deliveryModeOptions() {
      return deliveryMode.map(item => ({ label: this.$t(item.label), value: item.value }))
    },
    frequencyOptions() {
      return frequency.map(item => ({ label: this.$t(item.label), value: item.value }))
    },
    translatedAccountIntroducerTypeOptions() {
      return this.accountIntroducerTypeOptions.map(item => ({ label: this.$t(item.label), value: item.value }))
    },
    translatedSignatoryListHeaders() {
      return this.signatoryListHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedSignatoryMandateTableHeaders() {
      return this.signatoryMandateTableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedMandateListHeaders() {
      return this.mandateListHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.handleSearch()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.handleSearch()
    },
    // eslint-disable-next-line func-names
    'customer.accountOfficerID': function (newVal) {
      if (!Array.isArray(this.accountOfficerOptions)) return

      const exists = this.accountOfficerOptions.some(
        officer => officer.value === newVal,
      )
      if (!exists && newVal) {
        this.customer.accountOfficerID = null
      }
    },

    accountOfficerOptions(newOptions) {
      if (!Array.isArray(newOptions)) return

      const selected = this.customer.accountOfficerID
      const exists = newOptions.some(
        officer => officer.value === selected,
      )
      if (!exists && selected) {
        this.customer.accountOfficerID = null
      }
    },
  },
  beforeDestroy() {
    clearTimeout(this.timeout)
  },
  async mounted() {
    const { type, currencyOptions } = this.$route.params
    this.actionType = type
    this.currencyCodeOptions = currencyOptions
    if (this.actionType === 'edit') {
      this.showSpinner = true
      this.showSearchButton = true
    }
    if (this.actionType === 'create') {
      this.showSpinner = true
      this.showVirtualAccountWizard = true
      this.getVirtualAccountDetails()
    }
    this.getPermission()
    this.fetchAccountOfficers()
    // this.fetchCustomerAccounts()
    await this.fetchProducts()
    this.fetchBranches()
    this.getCustomerAccountDetails()
    this.fetchAccountMaintenanceFee()
  },
  methods: {
    ...mapActions({
      getBranches: 'operations/getBranchList',
      getCustomer: 'account/getCustomerList',
      getProducts: 'account/getProducts',
      getAccountOfficers: 'account/getAccountOfficerList',
      createCustomerAccount: 'account/createCustomerAccountAction',
      updateCustomerAccount: 'account/updateCustomerAccountAction',
      getGroupMembers: 'account/getGroupMembersByGroupId',
      getCustomerAccount: 'account/getCustomerAccountAction',
      generateMandateRuleAction: 'account/generateMandateRuleAction',
      getAccountSignatories: 'account/getAccountSignatoriesAction',
      getAccountReferees: 'account/getAccountRefereesAction',
      getCustomerInformation: 'account/getCustomerInformation',
      getCAMFee: 'account/getCAMFeeList',
      getCAMFeeById: 'account/getCAMFeeById',
      getCreditInterest: 'account/getInterestList',
      getInterestById: 'account/getInterestById',
      getCAMFeeByCode: 'account/getCAMFeeList',
      getCustomerAccounts: 'account/getCustomerAccountList',
      getAccountsByCustomerID: 'account/getAccountsByCustomerIDAction',
      getAccountOfficerByStaffID: 'account/getAccountOfficerByStaffID',
      uploadDocumentImages: 'account/uploadDocumentImagesAction',
      deleteDocumentsUpload: 'account/deleteDocumentsUploadAction',
      validateBVN: 'eChannels/validateBVNAction',
      createVirtualAccount: 'account/createVirtualAccountAction',
      getDocumentFiles: 'account/getDocumentFilesAction',
    }),
    onOverridePhoneNumberChange() {
      this.isPhoneReadonly = !this.overridePhoneNumber
    },
    onOverrideEmailChange() {
      this.isEmailReadonly = !this.overrideEmailAddress
    },
    addNewDocument() {
      this.documentsFormData.push({
        type: '',
        url: '',
        file: '',
      })
    },
    viewDocumentFile(image, title, fileExtension) {
      this.fileExtension = fileExtension
      this.viewDocumentData = image
      this.viewDocumentModalTitle = title ? title.replace(/([a-zA-Z])(?=[A-Z])/g, '$1 ') : ''
      this.$root.$emit('bv::show::modal', 'view-avatar-modal')
    },
    resetDocumentModal() {
      this.viewDocumentData = ''
      this.viewDocumentModalTitle = ''
    },
    handleDownload(data) {
      const link = document.createElement('a')
      link.href = data
      link.click()
      this.alertSuccess({ text: this.$t('Your request to download file was successful') })
    },
    async removeDocument(id, index) {
      try {
        this.documentLoadSpinner = true
        const formData = new FormData()
        formData.append('id', id)
        const response = await this.deleteDocumentsUpload({ formData, permission: accountPermissions.VIEW_SAVING_CURRENT_ACCOUNT_NIB_NIB })
        const { status } = response
        if (status === 200) {
          this.documentLoadSpinner = false
          this.documentsFormData.splice(index, 1)
          this.alertSuccess({ text: this.$t('Document Removed') })
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          this.alertError({ text: `${this.$t('Delete failed, Reason:')} ${responseError.errorMessage}` })
        })
      } finally {
        this.documentLoadSpinner = false
      }
    },
    async deleteDocumentFile(id, index) {
      try {
        this.documentLoadSpinner = true
        const formData = new FormData()
        formData.append('id', id)
        const response = await this.deleteDocumentsUpload({ formData, permission: accountPermissions.VIEW_SAVING_CURRENT_ACCOUNT_NIB_NIB })
        const { status } = response
        if (status === 200) {
          this.documentsFormData[index].url = ''
          this.documentLoadSpinner = false
          this.alertSuccess({ text: this.$t('Document Deleted') })
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          this.alertError({ text: `${this.$t('Delete failed, Reason:')} ${responseError.errorMessage}` })
        })
      } finally {
        this.documentLoadSpinner = false
      }
    },

    async onDocumentsUpload(index, event) {
      try {
        this.documentLoadSpinner = true
        const { files } = event.target

        const formData = new FormData()
        formData.append('attachedFile', files[0])
        formData.append('type', this.documentsFormData[index].type)
        formData.append('referenceNumber', this.documentIdentifier)
        const response = await this.uploadDocumentImages({ formData, permission: accountPermissions.VIEW_SAVING_CURRENT_ACCOUNT_NIB })
        const { status, data } = await response
        if (status === 200 && data) {
          this.documentsFormData[index].url = data.url
          this.documentsFormData[index].id = data.id
          this.documentsFormData[index].fileExtension = data.fileExtension
          this.documentLoadSpinner = false
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          this.alertError({ text: `${this.$t('Your request to upload image failed. Reason:')} ${responseError.errorMessage}` })
        })
      } finally {
        this.documentLoadSpinner = false
      }
    },
    onViewInfo() {
      this.$root.$emit('bv::show::modal', 'info-modal')
    },
    getLGAByState(selectedValue) {
      const country = countries.find(item => item.name === 'Nigeria')
      const selectedState = country.states.find(item => item.state === selectedValue)
      this.lgaOptions = selectedState.lgas.sort()
    },
    getPermission() {
      if (this.actionType === 'add') {
        this.permission = accountPermissions.CREATE_SAVING_CURRENT_ACCOUNT_NIB
        return
      }
      if (this.actionType === 'edit') {
        this.permission = accountPermissions.MODIFY_SAVING_CURRENT_ACCOUNT_NIB
        return
      }
      this.permission = accountPermissions.VIEW_SAVING_CURRENT_ACCOUNT_NIB
    },
    async setCreditInterestFee(selectedValue) {
      try {
        const { data } = await this.getInterestById({ id: selectedValue, permission: accountPermissions.VIEW_SAVING_CURRENT_ACCOUNT_NIB })

        this.interestRate = data.data.rate.toString()
        this.customer.interestCode = data.data.code
      } catch (error) {
        this.toastAlert({ title: error })
      }
    },
    setDefaultApplicableInterest(value) {
      if (!value) {
        this.customer.applicableInterest = null
        return false
      }
      return true
    },
    setProductApplicableFee(value) {
      if (!value) {
        this.customer.applicableFee = null
        return false
      }
      return true
    },
    showSearchField() {
      this.showSearchButton = false
      this.resetSearchParams()
    },
    onViewGroupMembers() {
      this.$root.$emit('bv::show::modal', 'signatory-mandate-table')
    },
    onViewPassport(image, name) {
      this.avatarLg = image
      this.avatarModalTitle = `${name}'s ${this.$t('Passport')}`
      this.$root.$emit('bv::show::modal', 'avatar-modal')
    },
    onViewSignature(image, name) {
      this.avatarLg = image
      this.avatarModalTitle = `${name}'s ${this.$t('Signature')}`
      this.$root.$emit('bv::show::modal', 'avatar-modal')
    },
    setAmountLimit(selectedValue) {
      if (!selectedValue) {
        const index = this.mandate.ruleConfig.findIndex(item => item.isLimited === selectedValue)

        if (index > -1) {
          this.mandate.ruleConfig[index].amount = 0
          return false
        }
      }
      return true
    },
    onViewEmailInfo() {
      this.$root.$emit('bv::show::modal', 'info-email-modal')
    },
    onViewPhoneInfo() {
      this.$root.$emit('bv::show::modal', 'info-phone-modal')
    },
    setPhoneNumber(val) {
      this.virtualAccountFormData.phoneNumber = val
      this.virtualAccountFormData.notificationPhoneNumber = this.virtualAccountFormData.phoneNumber
    },
    setEmailAddress(val) {
      this.virtualAccountFormData.email = val
      this.virtualAccountFormData.notificationEmail = this.virtualAccountFormData.email
    },
    handleSearch(type) {
      try {
        const fetchData = async () => {
          const isSearchParamsNotEmpty = Object.values(this.searchParams).every(params => (params === null || params === ''))
          if (!isSearchParamsNotEmpty) {
            this.showFormErrorAlert = false
            this.showSpinner = true
            // if "search customer" is changed at anytime,
            // kindly check and change where else it applied.
            this.searchType = 'search customer'
            const hasFetchedCustomer = await this.fetchCustomer()
            this.showSpinner = false
            if (hasFetchedCustomer) {
              this.$root.$emit('bv::show::modal', 'search-modal')
            }
          } else {
            this.showFormErrorAlert = true
          }
        }
        if (type === 'search') {
          this.$refs.searchRules.validate().then(async success => {
            if (success) {
              fetchData()
            }
          })
        } else {
          fetchData()
        }
      } catch (error) {
        this.showSpinner = false
        this.alertError({ text: error })
      } finally {
        this.showSpinner = false
      }
    },
    removeRefereeItem(index) {
      this.refereeList.splice(index, 1)
    },
    removeCustomerItem(index) {
      this.customerIntroducerList.splice(index, 1)
    },
    setApplyCreditInterest(selectedValue) {
      if (selectedValue === 'True') {
        this.isCreditInterest = true
        this.isConditional = false
        this.showAddProductInterest = false
        this.customer.productInterests = []
      } else if (selectedValue === 'False') {
        this.isCreditInterest = false
        this.isConditional = false
        this.showAddProductInterest = false
        this.customer.interestRate = 0
        this.customer.productInterests = []
      } else {
        this.isCreditInterest = false
        this.isConditional = true
        this.customer.interestRate = 0
      }
    },
    setApplyFee(selectedValue) {
      if (selectedValue === 'True') {
        this.isApplyFee = true
        this.isConditionalFee = false
        this.customer.productFees = []
      } else if (selectedValue === 'False') {
        this.isApplyFee = false
        this.isConditionalFee = false
        this.customer.productFees = []
        this.customer.flatAmount = null
        this.customer.flatAmountPerTransaction = null
        this.customer.percentageOnTurnover = null
        this.customer.maximumAmount = null
        this.customer.minimumAmount = null
      } else {
        this.isApplyFee = false
        this.isConditionalFee = true
        this.customer.flatAmount = null
        this.customer.flatAmountPerTransaction = null
        this.customer.percentageOnTurnover = null
        this.customer.maximumAmount = null
        this.customer.minimumAmount = null
      }
    },
    async fetchCustomer() {
      try {
        // if "search customer" is changed at anytime,
        // kindly check and change where else it applied.
        const response = await this.getCustomer({
          search: this.searchType === 'search customer' ? this.searchParams : this.refereeSearchFormData,
          page: this.currentPage,
          limit: this.perPage,
          permission: accountPermissions.VIEW_SAVING_CURRENT_ACCOUNT_NIB,
        })

        const { data, status } = await response
        if (status === 200 && data.data) {
          this.customerList = data.data.map(item => ({
            ...item,
            customerName: item.name,
            phoneNumber: item.customerType === 'Individual' ? item.phoneNumber : item.businessPhoneNumber,
          }))
          const customerIdentifier = data.data.reduce((acc, curr) => ({ documentIdentifier: curr.documentIdentifier }), {})
          // if "search customer" is changed at anytime,
          // kindly check and change where else it applied.
          if (this.searchType === 'search customer') {
            this.getDocumentByDocumentIdentifier(customerIdentifier.documentIdentifier)
          }
          this.totalCount = data.recordCount
          return true
        }
        this.alertError({ text: this.$t('No record found') })
        return false
      } catch (error) {
        this.handleError(error)
      } finally {
        this.showSpinner = false
      }
      return false
    },
    async getDocumentByDocumentIdentifier(documentIdentifier) {
      this.documentIdentifier = documentIdentifier
      try {
        const response = await this.getDocumentFiles({
          referenceNumber: documentIdentifier,
          permission: accountPermissions.VIEW_SAVING_CURRENT_ACCOUNT_NIB,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.documentsFormData = data.data ? data.data.map(item => ({
            url: item.url,
            type: item.typeID,
            fileName: item.type,
            id: item.id,
            fileExtension: item.fileExtension,
          })) : [{
            url: '',
            type: '',
          }]
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async onSelectCustomer(items) {
      const customerInfo = { ...items[0] }
      // if "search customer" is changed at anytime,
      // kindly check and change where else it applied.
      if (this.searchType === 'search customer') {
        this.customer.customerID = customerInfo.customerID
        this.customer.accountName = customerInfo.customerName
        this.customerType = customerInfo.customerType
        this.showCustomerInfo = true
        this.showSearchButton = true
        this.showBackButton = true
        this.hideModal('search-modal')

        if (this.customerType.toLowerCase() !== 'individual') {
          this.showSpinner = true
          await this.fetchGroupMembers(customerInfo.customerID)
          this.showSpinner = false
        }
        if (this.customerType.toLowerCase() === 'individual') {
          this.signatoryMandateList = [{
            fullName: customerInfo.name,
            passport: customerInfo.passportFileUrl,
            signature: customerInfo.signatureFileUrl,
          }]
        }
      } else if (this.searchType === 'referee') {
        this.hideModal('search-modal')
        if (this.refereeList.length <= 1) {
          const existingReferee = this.refereeList.find(item => item.customerName === customerInfo.customerName)
          if (existingReferee === undefined) {
            this.refereeList.push(customerInfo)
          } else {
            this.alertError({ text: this.$t('User has already been added as referee') })
          }
        } else {
          this.alertError({ text: this.$t('Maximum number of Referee is 2') })
        }
      } else if (this.searchType === 'account-introducer') {
        this.hideModal('search-modal')
        if (this.customerIntroducerList.length <= 1) {
          const existingCustomer = this.customerIntroducerList.find(item => item.customerID === customerInfo.customerID)
          if (existingCustomer === undefined) {
            this.customer.introducerName = customerInfo.name
            this.customer.introducerID = customerInfo.customerID
            this.customerIntroducerList.push(customerInfo)
          } else {
            this.alertError({ text: this.$t('User has already been added as a Customer') })
          }
        } else {
          this.alertError({ text: this.$t('Maximum number of Customer is 1') })
        }
      }
    },
    async fetchGroupMembers(id) {
      const formData = {
        groupCustomerID: id,
        pageNumber: 1,
        pageSize: 20,
      }

      const response = await this.getGroupMembers({
        formData, permission: accountPermissions.VIEW_SAVING_CURRENT_ACCOUNT_NIB,
      })
      const { data, status } = response
      if (status === 200 && data.data) {
        this.signatoryOptions = data.data?.map(item => item.name)
        this.signatories = data.data?.map(item => (item.name)) // used to hold signatories values
        this.groupMembers = data.data?.map(item => ({ label: item.name, value: item.customerID }))

        this.signatoryMandateList = data.data?.map(item => ({
          fullName: item.name,
          passport: item.passportFileUrl,
          signature: item.signatureFileUrl,
        }))
      }
    },
    createSignatoryAction() {
      this.$refs.signatoryRules.validate().then(async success => {
        if (success) {
          // this.mandate.ruleConfig[0].categoryConfig.find((item) => item)
          this.groupMembers.map(item => {
            if (item.label === this.signatory) {
              this.selectedSignatories.push({ id: item.value, signatory: this.signatory, category: this.category })
              return { id: item.value, signatory: this.signatory, category: this.category }
            }
            return ''
          })
          this.signatoryList = [
            ...this.signatoryList,
            { signatory: this.signatory, category: this.category },
          ]

          this.signatoryOptions = this.signatoryOptions.filter(item => item !== this.signatory)
          if (!this.signatoryOptions.length) {
            this.signatory = ''
            this.signatoryRule = ''
          }
          this.mandate.ruleConfig[0].categoryConfig.push(
            {
              category: this.category,
              authorizableNumber: 0,
            },
          )
        }
      })
    },
    setIntroducerType(value) {
      if (value) {
        this.customer.introducerID = ''
      }
    },
    async fetchStaffName(staffID) {
      try {
        const response = await this.getAccountOfficerByStaffID({ staffID, permission: accountPermissions.VIEW_SAVING_CURRENT_ACCOUNT_NIB })
        const { data, status } = response
        if (status === 200 && data.data !== null) {
          this.customer.introducerName = data.data.name
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    removeSignatoryAction(items) {
      const removeSignatory = () => {
        const selectedSignatoryIndex = this.signatoryList.findIndex(item => item.signatory === items.signatory)
        const selectedSignatoryItem = this.signatoryList.find(item => item.signatory === items.signatory)
        const selectedMandateConfigIndex = this.mandate.ruleConfig[0].categoryConfig.findIndex(item => item.category.toLowerCase() === items.category.toLowerCase())

        this.signatoryList.splice(selectedSignatoryIndex, 1)
        this.mandate.ruleConfig[0].categoryConfig.splice(selectedMandateConfigIndex, 1)
        this.signatoryOptions.push(selectedSignatoryItem.signatory)
        this.mandateList = this.mandateList.filter(item => !item.rule.includes(items.category))
        this.selectedSignatories = this.selectedSignatories.filter(item => item.category !== items.category)

        if (!this.mandateList.length) {
          this.signatoryList = []
          this.signatoryOptions = this.signatories
        }
        this.signatoryRule = 'required'
      }
      this.confirm(removeSignatory, { title: '', text: this.$t('Are you sure you want to remove this signatory?') })
    },
    removeMandateAction(items) {
      const removeMandate = () => {
        const selectedMandateIndex = this.mandateList.findIndex(item => item.amount === items.amount)
        const selectedMandateConfigIndex = this.customer.mandateRuleConfig.ruleConfig.findIndex(item => item.amount === items.amount)
        this.mandateList.splice(selectedMandateIndex, 1)
        this.customer.mandateRuleConfig.ruleConfig.splice(selectedMandateConfigIndex, 1)
        this.signatoryList = this.signatoryList.filter(item => !items.rule.includes(item.category))
        this.selectedSignatories = this.selectedSignatories.filter(item => item.category !== items.category)

        if (!this.signatoryList.length) {
          this.mandateList = []
          this.signatoryOptions = this.signatories
        }
      }

      this.confirm(removeMandate, { title: '', text: this.$t('Are you sure you want to remove this mandate configuration?') })
    },
    async fetchAccountOfficers() {
      try {
        const response = await this.getAccountOfficers({
          search: '',
          page: 0,
          limit: 0,
          permission: accountPermissions.VIEW_SAVING_CURRENT_ACCOUNT_NIB,
        })
        const { data, status } = await response
        if (status === 200 && data.data) {
          // eslint-disable-next-line no-nested-ternary
          this.accountStaffOptions = data.data.filter(item => item.status === 'Active').map(item => ({ label: item.name, value: item.staffID })).sort((a, b) => (a.label === b.label ? 0 : a.label < b.label ? -1 : 1))
          // eslint-disable-next-line no-nested-ternary
          this.accountOfficerOptions = data.data.filter(item => item.status === 'Active').map(item => ({ label: item.name, value: item.id })).sort((a, b) => (a.label === b.label ? 0 : a.label < b.label ? -1 : 1))
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    async fetchBranches() {
      try {
        const response = await this.getBranches({
          search: '',
          page: 0,
          limit: 0,
          permission: accountPermissions.VIEW_SAVING_CURRENT_ACCOUNT_NIB,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.branchOptions = data.data.filter(item => item.status === 'Active').map(item => ({
            label: item.name,
            value: item.code,
          // eslint-disable-next-line no-nested-ternary
          })).sort((a, b) => (a.label === b.label ? 0 : a.label < b.label ? -1 : 1))
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    async fetchProducts() {
      try {
        const response = await this.getProducts({
          search: '',
          page: 0,
          limit: 0,
          permission: accountPermissions.VIEW_SAVING_CURRENT_ACCOUNT_NIB,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.productTypes = data.data.filter(item => item.type === 'Savings' && item.status === 'Active')
          this.productTypeOptions = data.data.filter(item => item.type === 'Savings' && item.status === 'Active').map(item => ({ label: item.name, value: item.code.toString() }))
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    handleSubmit() {
      // eslint-disable-next-line no-unused-expressions
      this.actionType === 'add' ? this.signatoryRule = 'required' : this.signatoryRule = ''
      const buttonLabel = this.actionType === 'edit' ? this.$t('Modify') : this.$t('Save')
      const textA = this.$t('Are you sure you want to')
      const textB = this.$t('this Customer Account?')
      if (this.customerType !== 'Individual' && this.showEditable === true) {
        this.confirm(this.customerAccountAction, { text: `${textA} ${buttonLabel} ${textB}` })
      } else {
        this.confirm(this.customerAccountAction, { text: `${textA} ${buttonLabel} ${textB}` })
      }
    },
    async customerAccountAction() {
      try {
        this.isLoading = true
        this.isDisabled = true
        let signatories
        let referees
        if (this.signatoryList.length) {
          signatories = this.selectedSignatories.map(item => ({
            signatoryCustomerID: item.id,
            category: item.category,
          }))
        }
        if (this.refereeList.length) {
          referees = this.refereeList.reduce((acc, item, index) => {
            const dynamicKey = `referee${index + 1}CustomerID`
            const refereeInformation = { ...acc, [dynamicKey]: item.customerID }

            return refereeInformation
          }, {})
        }
        let productFees
        let productInterests
        if (this.customer.productFees && this.customer.productFees.length > 0) {
          productFees = this.customer.productFees.map(item => ({
            ...item,
            feeConditionBasis: this.feeConditionBasis,
            feeConditionValue: !this.isFeeNoOfWithdrawal ? toKobo(item.feeConditionValue) : item.feeConditionValue,
            flatAmount: toKobo(item.flatAmount),
            flatAmountPerTransaction: toKobo(item.flatAmountPerTransaction),
            maximumAmount: toKobo(item.maximumAmount),
            minimumAmount: toKobo(item.minimumAmount),
          }))
        } else {
          productFees = []
        }
        const minimumBalanceRequired = this.customer.minimumBalanceRequired === 0 ? 0 : toKobo(this.customer.minimumBalanceRequired)
        if (this.customer.productInterests && this.customer.productInterests.length > 0) {
          productInterests = this.customer.productInterests.map(item => ({
            ...item,
            interestConditionBasis: this.interestConditionBasis,
            interestConditionValue: !this.isInterestNoOfWithdrawal ? toKobo(item.interestConditionValue) : item.interestConditionValue,
            useDefaultInterestRate: false,
          }))
        } else {
          productInterests = []
        }

        const formData = {
          ...this.customer,
          documentIdentifier: this.documentIdentifier,
          introducerName: this.customer.introducerName,
          introducerID: this.customer.introducerID,
          minimumBalanceRequired,
          mandateRuleConfig: { ruleConfig: transformMandateRuleConfig(this.mandateList) },
          refereeInformation: referees,
          signatories: {
            data: signatories || [],
          },
          createdBy: this.userDetails.email,
        }
        const { status } = this.actionType === 'save' || this.actionType === 'add'
          ? await this.createCustomerAccount(formData)
          : await this.updateCustomerAccount({
            ...formData,
            minimumBalanceRequired,
            minimumAmount: toKobo(this.customer.minimumAmount),
            maximumAmount: toKobo(this.customer.maximumAmount),
            flatAmount: toKobo(this.customer.flatAmount),
            flatAmountPerTransaction: toKobo(this.customer.flatAmountPerTransaction),
            productFees: !this.customer.overrideProductApplicableFee ? null : productFees,
            productInterests: !this.customer.overrideProductApplicableInterest ? null : productInterests,
          })

        if (status === 200) {
          const textA = this.$t('Your request to')
          const textB = this.$t('Customer Account was successful')
          this.alertSuccess({ text: `${textA} ${this.actionType} ${textB}` })
          this.isLoading = false
          this.isDisabled = false
          this.$router.push({ name: 'current-savings-account-list-nib' })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isLoading = false
        this.isDisabled = false
      }
    },
    async createRuleInfoAction() {
      this.$refs.ruleConfigurationRules.validate().then(async success => {
        if (success) {
          this.isAddRuleDisabled = true
          this.isAddRuleLoading = true
          const filteredCategoryConfig = this.mandate.ruleConfig[0].categoryConfig.filter(item => item.authorizableNumber !== 0)
          if (filteredCategoryConfig.length) {
            const formData = {
              ruleConfig: [
                {
                  categoryConfig: filteredCategoryConfig,
                  isLimited: this.mandate.ruleConfig[0].isLimited,
                  amount: this.mandate.ruleConfig[0].amount,
                },
              ],
            }
            const response = await this.generateMandateRuleAction({ formData, permission: accountPermissions.VIEW_SAVING_CURRENT_ACCOUNT_NIB })

            this.customer.mandateRuleConfig.ruleConfig.push(formData.ruleConfig[0])
            const { data, status } = response
            if (status === 200) {
              data.data.forEach(item => {
                this.mandateList.push(item)
              })
            }
          } else {
            this.alertError({ text: this.$t('Please enter a value for customer count to proceed') })
          }
        }
      }).catch(error => {
        this.alertError({ text: error })
      }).finally(() => {
        this.isAddRuleDisabled = false
        this.isAddRuleLoading = false
      })
    },
    async handleRefereeSearch() {
      try {
        const isSearchParamsNotEmpty = Object.values(this.refereeSearchFormData).every(params => (params === null || params === ''))
        if (!isSearchParamsNotEmpty) {
          this.showFormErrorAlert = false
          this.hideModal('search-modal')
          this.showSpinner = true
          this.searchType = 'referee'
          await this.fetchCustomer()
          this.showSpinner = false
          this.$root.$emit('bv::show::modal', 'search-modal')
        } else {
          this.showFormErrorAlert = true
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.showSpinner = false
      }
    },
    async handleCustomerSearch() {
      try {
        const isSearchParamsNotEmpty = Object.values(this.refereeSearchFormData).every(params => (params === null || params === ''))
        if (!isSearchParamsNotEmpty) {
          this.showFormErrorAlert = false
          this.hideModal('search-modal')
          this.showSpinner = true
          this.searchType = 'account-introducer'
          await this.fetchCustomer()
          this.showSpinner = false
          this.$root.$emit('bv::show::modal', 'search-modal')
        } else {
          this.showFormErrorAlert = true
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.showSpinner = false
      }
    },
    async getVirtualAccountDetails() {
      const { items } = this.$route.params
      if (items !== undefined) {
        // if "search customer" is changed at anytime,
        // kindly check and change where else it applied.
        this.searchType = 'search customer'
        this.searchParams = {
          customerID: items.customerID,
        }
        this.fetchCustomer()
        this.virtualAccountFormData = {
          id: items.id,
          customerID: items.customerID,
          accountName: items.accountName,
          alias: items.alias,
          parentAccountNumber: items.accountNumber,
          referenceNumber: items.referenceNumber,
          // accountType: 'VirtualAccount',
          branchCode: items.branchCode,
          productCode: items.productCode,
          accountStatus: items.accountStatus,
          accountOfficerID: items.accountOfficerID,
          accountTierLevel: 1,
          employeeID: items.employeeID,
        }
        this.showVirtualAccountWizard = true
        this.showSearchButton = true
      }
    },
    handleVirtualAccount() {
      this.confirm(this.virtualAccountAction, { text: this.$t('Are you sure you want to Save this Virtual Account?') })
    },
    async virtualAccountAction() {
      const textA = this.$t('Your request to Save Virtual Account for')
      const textB = this.$t('with CustomerID')
      const textC = this.$t('was successful')
      const textD = this.$t('Your request to Save Virtual Account failed. Reason:')
      try {
        this.isLoading = true
        this.isDisabled = true
        const formData = {
          ...this.virtualAccountFormData,
          documentIdentifier: this.documentIdentifier,
          dateOfBirth: this.virtualAccountFormData.dateOfBirth ? toYearWithDate(this.virtualAccountFormData.dateOfBirth) : null,
        }
        const fullName = `${this.virtualAccountFormData?.lastName ?? ''} ${this.virtualAccountFormData?.firstName ?? ''} ${this.virtualAccountFormData?.otherNames ?? ''}`
        const response = await this.createVirtualAccount(formData)
        const { status, data } = response

        if (status === 200 && data) {
          this.alertSuccess({ text: `${textA} ${fullName} ${textB}: ${this.virtualAccountFormData.customerID} ${textC}` })
          this.$router.push({ name: 'current-savings-account-list-nib' })
        }
      } catch (error) {
        this.isLoading = false
        this.isDisabled = false
        error.response.data.errors.forEach(responseError => {
          this.alertError({ text: `${textD} ${responseError.errorMessage}` })
        })
      } finally {
        this.isLoading = false
        this.isDisabled = false
      }
    },
    setAccountLastName(val) {
      const { alias } = this.virtualAccountFormData
      const setAlias = alias ? `${alias} /` : ''
      this.virtualAccountFormData.virtualAccountName = `${setAlias} ${val} ${this.virtualAccountFormData.firstName ? this.virtualAccountFormData.firstName : ''} ${this.virtualAccountFormData.otherNames ? this.virtualAccountFormData.otherNames : ''}`
    },
    setAccountFirstName(val) {
      const { alias } = this.virtualAccountFormData
      const setAlias = alias ? `${alias} /` : ''
      this.virtualAccountFormData.virtualAccountName = `${setAlias} ${this.virtualAccountFormData.lastName ? this.virtualAccountFormData.lastName : ''} ${val} ${this.virtualAccountFormData.otherNames ? this.virtualAccountFormData.otherNames : ''}`
    },
    setAccountOtherNames(val) {
      const { alias } = this.virtualAccountFormData
      const setAlias = alias ? `${alias} /` : ''
      this.virtualAccountFormData.virtualAccountName = `${setAlias} ${this.virtualAccountFormData.lastName ? this.virtualAccountFormData.lastName : ''} ${this.virtualAccountFormData.firstName ? this.virtualAccountFormData.firstName : ''} ${val}`
    },
    async getCustomerAccountDetails() {
      try {
        const { id, type } = this.$route.params
        if (id !== undefined) {
          this.actionType = type
          // this.showSpinner = true
          this.showCustomerInfo = true
          this.showSearchButton = true
          this.showBackButton = true
          this.isEditable = true
          this.showEditable = type === 'edit'

          const { data, status } = await this.getCustomerAccount({ id, permission: accountPermissions.VIEW_SAVING_CURRENT_ACCOUNT_NIB })

          let productInterests
          let productFees
          if (status === 200 && data.data) {
            // const selectedProduct = this.productTypes.find(item => item.code.toString() === data.data.productCode)
            await this.fetchGroupMembers(data.data.customerID)

            this.customer.applyCreditInterest = data.data.applyCreditInterest
            this.customer.applyFee = data.data.applyFee

            // if (data.data.applyFee === null && data.data.applyCreditInterest === null) {
            //   productFees = []
            // }

            if (data.data.applyCreditInterest === 'True') {
              this.setApplyCreditInterest(data.data.applyCreditInterest)
              this.customer.interestRate = data.data.interestRate
            } else if (data.data.applyCreditInterest !== null) {
              this.setApplyCreditInterest(data.data.applyCreditInterest)
              this.customer.applyCreditInterest = data.data.applyCreditInterest

              if (data.data?.productInterests && data.data?.productInterests.length > 0) {
                this.interestConditionBasis = data.data?.productInterests[0].interestConditionBasis
                this.isInterestNoOfWithdrawal = data.data?.productInterests[0].interestConditionBasis === 'NoOfWithdrawals'

                productInterests = data.data?.productInterests?.map(item => ({
                  ...item,
                  interestConditionValue: !this.isInterestNoOfWithdrawal ? formatAmount(item.interestConditionValue) : item.interestConditionValue,
                  interestRate: convertZeroToString(item.interestRate),
                }))

                this.customer.productInterests = productInterests
              }
            } else {
              this.setApplyCreditInterest(data.data.applyCreditInterest)
              this.customer.productInterests = []
            }

            if (data.data.applyFee === 'True') {
              this.setApplyFee(data.data.applyFee)
              this.customer.flatAmount = data.data.flatAmount === 0 ? '0' : formatAmount(data.data.flatAmount)
              this.customer.flatAmountPerTransaction = data.data.flatAmountPerTransaction === 0 ? '0' : formatAmount(data.data.flatAmountPerTransaction)
              this.customer.percentageOnTurnover = data.data.percentageOnTurnover === 0 ? '0' : data.data.percentageOnTurnover
              this.customer.maximumAmount = data.data.maximumAmount === 0 ? '0' : formatAmount(data.data.maximumAmount)
              this.customer.minimumAmount = data.data.minimumAmount === 0 ? '0' : formatAmount(data.data.minimumAmount)
            } else if (data.data.applyFee !== null) {
              this.setApplyFee(data.data.applyFee)
              if (data.data?.productFees && data.data?.productFees.length > 0) {
                this.feeConditionBasis = data.data?.productFees[0].feeConditionBasis
                this.isFeeNoOfWithdrawal = data.data?.productFees[0].feeConditionBasis === 'NoOfWithdrawals'
                productFees = data.data?.productFees?.map(item => ({
                  ...item,
                  flatAmount: item.flatAmount === 0 ? '0' : formatAmount(item.flatAmount),
                  flatAmountPerTransaction: item.flatAmountPerTransaction === 0 ? '0' : formatAmount(item.flatAmountPerTransaction),
                  percentageOnTurnover: convertZeroToString(item.percentageOnTurnover),
                  minimumAmount: item.minimumAmount === 0 ? '0' : formatAmount(item.minimumAmount),
                  maximumAmount: item.maximumAmount === 0 ? '0' : formatAmount(item.maximumAmount),
                  feeConditionValue: !this.isFeeNoOfWithdrawal ? formatAmount(item.flatAmount) : convertZeroToString(item.feeConditionValue),
                }))

                this.customer.productFees = productFees
              }
            } else {
              this.setApplyFee(data.data.applyFee)
              this.customer.productFees = []
            }

            this.customer = {
              ...this.customer,
              id: data.data.id,
              customerID: data.data.customerID,
              accountName: data.data.accountName,
              referenceNumber: data.data.referenceNumber,
              accountType: data.data.accountType,
              branchCode: data.data.branchCode,
              productCode: data.data.productCode,
              accountStatus: data.data.accountStatus,
              accountOfficerID: data.data.accountOfficerID,
              introducerType: data.data.introducerType,
              introducerName: data.data.introducerName,
              introducerID: data.data.introducerID,
              accountTierLevel: data.data.accountTierLevel,
              accessLevel: data.data.accessLevel,
              sectorCode: data.data.sectorCode,
              categoryOfAccount: data.data.categoryOfAccount,
              signatories: {},
              overrideProductApplicableFee: data.data.overrideProductApplicableFee,
              applicableFee: data.data.applicableFee,
              overrideProductApplicableInterest: data.data.overrideProductApplicableInterest,
              applicableInterest: data.data.applicableInterest,
              statementDeliveryMode: data.data.statementDeliveryMode,
              statementDeliveryFrequency: data.data.statementDeliveryFrequency,
              enableEmailNotification: data.data.enableEmailNotification,
              enableSMSNotification: data.data.enableSMSNotification,
              notificationEmail: data.data.notificationEmail,
              notificationPhoneNumber: data.data.notificationPhoneNumber,
              minimumBalanceRequired: data.data.minimumBalanceRequired === 0 ? '0' : toBalance(data.data.minimumBalanceRequired),
              accountCreationChannel: data.data.accountCreationChannel,
              employeeID: data.data.employeeID,
              currencyCode: data.data.currencyCode,
              groupConnectionID: data.data.groupConnectionID,
              groupConnectionName: data.data.groupConnectionName,
              mandateRuleConfig: {
                ruleConfig: [],
              },
            }
            this.overridePhoneNumber = !!data.data.notificationPhoneNumber
            this.overrideEmailAddress = !!data.data.notificationEmail
            this.documentIdentifier = data.data.documentIdentifier ? data.data.documentIdentifier : this.documentIdentifier
            this.documentsFormData = data.data.documents ? data.data.documents.map(item => ({
              url: item.url,
              type: item.typeID,
              fileName: item.type,
              id: item.id,
              fileExtension: item.fileExtension,
            })) : [{
              url: '',
              type: '',
            }]

            if (data.data.isGroupAccount) {
              const customerSignatory = await this.getAccountSignatories({ accountNumber: data.data.accountNumber, permission: accountPermissions.VIEW_SAVING_CURRENT_ACCOUNT_NIB })
              const signatoryID = customerSignatory.data.data.map(item => item.customerID)
              this.signatoryOptions = this.groupMembers.filter(item => !signatoryID.includes(item.value)).map(customer => customer.label)
              this.signatoryList = customerSignatory.data.data.map(item => ({
                signatory: item.name,
                category: item.category,
              }))

              // this.signatoryMandateList = customerSignatory?.data.data?.map(item => ({
              //   fullName: item.name,
              //   passport: item.passportFileUrl,
              //   signature: item.signatureFileUrl,
              // }))
              this.totalSignatoriesCount = customerSignatory.data.data ? customerSignatory.data.data.recordCount : 0
            }

            if (!data.data.isGroupAccount) {
              const customerInfo = await this.getCustomerInformation({ customerID: data.data.customerID, permission: accountPermissions.VIEW_SAVING_CURRENT_ACCOUNT_NIB })
              this.signatoryMandateList = [{
                fullName: customerInfo.data.data.name,
                passport: customerInfo.data.data.passportFileUrl,
                signature: customerInfo.data.data.signatureFileUrl,
              }]
            }

            if (data.data.mandateRuleIndex !== null) {
              this.mandateList = transformMandateRule(data.data.mandateRuleIndex)

              this.customer.mandateRuleConfig.ruleConfig = transformMandateRuleConfig(this.mandateList)
            }
            this.fetchAccountReferees(data.data.accountNumber)
            if (data.data.introducerType === 'Customer') {
              this.fetchAccountByCustomerID(data.data.introducerID)
            }
            if (data.data.isGroupAccount) {
              this.customerType = 'Group'
              // const filterSignatory = this.signatoryList.map(item => {
              //   if (!this.signatoryOptions.includes(item.signatory)) {
              //     return item
              //   }
              //   return false
              // })
              // this.signatoryOptions = filterSignatory.filter(item => item !== false)
            }
            this.showSpinner = false
            this.$refs.casaAccount.activateAll()
          }
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.showSpinner = false
      }
    },
    async fetchAccountSignatories(accountNumber) {
      try {
        const response = await this.getAccountSignatories({ accountNumber, permission: accountPermissions.VIEW_SAVING_CURRENT_ACCOUNT_NIB })
        const { data, status } = response
        if (status === 200 && data.data) {
          this.signatoryList = data.data.map(item => ({
            ...item,
            signatory: `${item?.lastName ?? ''} ${item?.firstName ?? ''} ${item?.otherNames ?? ''}`,
          }))

          // this.selectedSignatories = data.data.map(item => ({
          //   id: item.customerID,
          //   signatory: `${item?.lastName ?? ''} ${item?.firstName ?? ''} ${item?.otherNames ?? ''}`,
          //   category: item.category,
          // }))

          // this.groupMembers = data.data.map(item => ({ label: `${item?.lastName ?? ''} ${item?.firstName ?? ''} ${item?.otherNames ?? ''}`, value: item.customerID }))
        }
        // else {
        //   this.signatoryList = []

        //   this.groupMembers = []
        // }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    async fetchAccountReferees(accountNumber) {
      try {
        const response = await this.getAccountReferees({ accountNumber, permission: accountPermissions.VIEW_SAVING_CURRENT_ACCOUNT_NIB })
        const { data, status } = response
        if (status === 200 && data.data !== null) {
          this.refereeList = data.data.map(item => ({
            ...item,
            customerName: `${item?.lastName ?? ''} ${item?.firstName ?? ''} ${item?.otherNames ?? ''}`,
          }))
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    async fetchAccountByCustomerID(customerID) {
      try {
        const response = await this.getAccountsByCustomerID({ customerID, permission: this.permission })
        const { data, status } = response
        if (status === 200 && data.data !== null) {
          this.customerIntroducerList.push({
            ...data.data,
            customerName: `${data.data?.lastName ?? ''} ${data.data?.firstName ?? ''} ${data.data?.otherNames ?? ''}`,
          })
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    setAccountOfficerName(selectedValue) {
      const selectedAccountOfficer = this.accountOfficerOptions.find(account => account.value === selectedValue)
      this.customer.accountOfficerName = selectedAccountOfficer ? selectedAccountOfficer.label : ''
    },
    setCustomerBranch(selectedValue) {
      const selectedBranch = this.branchOptions.find(branch => branch.value === selectedValue)
      this.customer.branchName = selectedBranch ? selectedBranch.label : ''
    },
    async setApplicableInterest(selectedValue) {
      try {
        const productType = this.productTypes.find(item => item.code === +selectedValue)
        this.customer.minimumBalanceRequired = productType.minimumBalance === 0 ? '0' : formatAmount(productType.minimumBalance)
        this.customer.currencyCode = productType.currencyCode ? productType.currencyCode : ''
        this.customer.applyCreditInterest = productType.applyCreditInterest
        this.customer.applyFee = productType.applyFee

        const selectedProduct = this.productTypeOptions.find(product => product.value === selectedValue)
        this.customer.productName = selectedProduct ? selectedProduct.label : ''

        // if (productType.applyCreditInterest === 'True') {
        //   await this.setCreditInterestFee(productType.id)
        // }

        if (productType.interests) {
          this.isInterestNoOfWithdrawal = productType.interests?.productInterests[0].interestConditionBasis === 'NoOfWithdrawals'

          this.customer.productInterests = productType.interests.map(item => ({
            ...item,
            interestConditionValue: !this.isInterestNoOfWithdrawal ? formatAmount(item.interestConditionValue) : item.interestConditionValue,
            interestRate: convertZeroToString(item.interestRate),

          }))
        }

        if (productType.fees) {
          this.isFeeNoOfWithdrawal = productType?.productFees[0].feeConditionBasis === 'NoOfWithdrawals'
          this.customer.productFees = productType.fees.map(item => ({
            ...item,
            flatAmount: item.flatAmount === 0 ? '0' : formatAmount(item.flatAmount),
            flatAmountPerTransaction: item.flatAmountPerTransaction === 0 ? '0' : formatAmount(item.flatAmountPerTransaction),
            percentageOnTurnover: convertZeroToString(item.percentageOnTurnover),
            minimumAmount: item.minimumAmount === 0 ? '0' : formatAmount(item.minimumAmount),
            maximumAmount: item.maximumAmount === 0 ? '0' : formatAmount(item.maximumAmount),
            feeConditionValue: !this.isFeeNoOfWithdrawal ? formatAmount(item.flatAmount) : convertZeroToString(item.feeConditionValue),
          }))
        }

        this.setApplyCreditInterest(productType.applyCreditInterest)
        this.setApplyFee(productType.applyFee)
        // this.interestConditionBasis = (productType.interests && productType.interests.length) ? productType.interests[0].interestConditionBasis : ''
        this.showAddProductInterest = productType.applyCreditInterest.toLowerCase().trim() === 'conditional'
        this.showAddProductFee = productType.applyFee.toLowerCase() === 'conditional'
        // this.isInterestNoOfWithdrawal = (productType.interests && productType.interests.length) ? productType.interests[0].interestConditionBasis : ''
        // this.feeConditionBasis = (productType.fees && productType.fees.length) ? productType.fees[0].feeConditionBasis : ''
        // if (productType.applyFee.toLowerCase().trim() === 'true') {
        //   await this.setCOTFees(productType.feeCode)
        // }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.showSpinner = false
      }
    },
    async setCAMFees(code) {
      try {
        const response = await this.getCAMFeeByCode({
          search: { code },
          page: 1,
          limit: 10,
          permission: accountPermissions.VIEW_SAVING_CURRENT_ACCOUNT_NIB,
        })
        const { status, data } = response

        const [camFee] = data.data

        if (status === 200) {
          this.customer.flatAmount = camFee.flatAmount
          this.customer.flatAmountPerTransaction = camFee.flatAmountPerTransaction.toString()
          this.customer.percentageOnTurnover = camFee.percentageOnTurnover.toString()
          this.customer.minimumAmount = camFee.minimumAmount.toString()
          this.customer.maximumAmount = camFee.maximumAmount.toString()
          this.customer.feeCode = camFee.code
          this.customer.transactionType = camFee.transactionType
          this.customer.chargeCycle = camFee.chargeCycle
        }
      } catch (error) {
        this.toastAlert({ title: error })
      }
    },
    async fetchAccountMaintenanceFee() {
      try {
        const response = await this.getCAMFee({
          search: '',
          page: 0,
          limit: 0,
          permission: accountPermissions.VIEW_SAVING_CURRENT_ACCOUNT_NIB,
        })
        const { status, data } = await response
        if (status === 200 && data.data.length) {
          this.feeDetails = data.data
          this.camFeeOptions = data.data.filter(dataItem => dataItem.status === 'Active').map(item => ({ label: item.name, value: item.id, status: item.status }))
        }
      } catch (error) {
        this.toastAlert({ title: error })
      }
    },
    repeatProductInterest() {
      this.customer.productInterests.push({
        interestConditionValue: null,
        // interestCode: null,
        // interestID: null,
        interestRate: null,
        useDefaultInterestRate: true,
      })
      // this.$nextTick(() => {
      //   this.trAddHeight(this.$refs.productInterest[0].offsetHeight)
      // })
    },
    repeatProductFees() {
      this.customer.productFees.push({
        // feeCode: '',
        // feeID: null,
        flatAmount: null,
        flatAmountPerTransaction: null,
        percentageOnTurnover: null,
        minimumAmount: null,
        maximumAmount: null,
        feeConditionValue: null,
      })
    },

    removeProductInterestItem(index) {
      this.customer.productInterests.splice(index, 1)
    },
    removeProductFeeItem(index) {
      this.customer.productFees.splice(index, 1)
    },
    resetRefereeModal() {
      this.refereeSearchFormData = { ...null }
    },
    resetSearchParams() {
      this.searchParams = { ...null }
    },
    resetAvatarModal() {
      this.avatarLg = ''
      this.avatarModalTitle = ''
    },
    setProductInterest(selectedValue) {
      this.isInterestNoOfWithdrawal = selectedValue === 'NoOfWithdrawals'
      this.showAddProductInterest = this.actionType === 'edit'
      this.customer.productInterests = []
    },
    setProductFee(selectedValue) {
      this.isFeeNoOfWithdrawal = selectedValue === 'NoOfWithdrawals'
      this.showAddProductFee = this.actionType === 'edit'
      this.customer.productFees = []
    },
    async setProductCAMFee(selectedValue) {
      try {
        const response = await this.getCAMFeeById({ id: selectedValue, permission: accountPermissions.VIEW_SAVING_CURRENT_ACCOUNT_NIB })
        const { status, data } = response

        if (status === 200 && Object.entries(data.data).length) {
          const index = this.customer.productFees.findIndex(item => item.feeID === selectedValue)

          this.customer.productFees[index].flatAmount = data.data.flatAmount.toString()
          this.customer.productFees[index].maximumAmount = data.data.maximumAmount.toString()
          this.customer.productFees[index].minimumAmount = data.data.minimumAmount.toString()
          this.customer.productFees[index].percentageOnTurnover = data.data.percentageOnTurnover.toString()
          this.customer.productFees[index].flatAmountPerTransaction = data.data.flatAmountPerTransaction.toString()
          this.customer.productFees[index].feeCode = data.data.code
        }
      } catch (error) {
        this.toastAlert({ title: error })
      }
    },
    async setProductCreditInterestFee(selectedValue) {
      try {
        const { status, data } = await this.getInterestById({ id: selectedValue, permission: accountPermissions.VIEW_SAVING_CURRENT_ACCOUNT_NIB })
        if (status === 200 && Object.entries(data.data).length) {
          const index = this.customer.productInterests.findIndex(item => item.interestID === selectedValue)

          this.customer.productInterests[index].interestRate = data.data.rate
          this.customer.productInterests[index].interestCode = data.data.code
        }
      } catch (error) {
        this.toastAlert({ title: error })
      }
    },
    validateAccountInformation() {
      return new Promise((resolve, reject) => {
        this.$refs.savingRules.validate().then(success => {
          if (success) {
            resolve(true)
          } else {
            reject()
          }
        })
      })
    },
    validateInterest() {
      return new Promise((resolve, reject) => {
        this.$refs.interestRules.validate().then(success => {
          if (success) {
            resolve(true)
          } else {
            reject()
          }
        })
      })
    },
    validateStatement() {
      return new Promise((resolve, reject) => {
        this.$refs.statementRules.validate().then(success => {
          if (success) {
            resolve(true)
          } else {
            reject()
          }
        })
      })
    },
    validatUploads() {
      return new Promise((resolve, reject) => {
        this.$refs.uploadRules.validate().then(success => {
          if (success) {
            resolve(true)
          } else {
            reject()
          }
        })
      })
    },
    validatePersonal() {
      return new Promise((resolve, reject) => {
        this.$refs.personalInfoRules.validate().then(success => {
          if (success) {
            resolve(true)
          } else {
            reject()
          }
        })
      })
    },
    bvnValidateAction() {
      if (this.virtualAccountFormData.bvn) {
        this.confirm(this.validateBVNAction, { text: this.$t('BVN validation comes at a cost to the bank!  Are you sure you want to proceed?') })
      } else {
        this.alertError({ text: this.$t('BVN field is empty') })
      }
    },
    async validateBVNAction() {
      try {
        this.isLoading = true
        this.showSpinner = true
        const formData = {
          bvnNumber: this.virtualAccountFormData.bvn,
        }
        const response = await this.validateBVN({ formData, permission: accountPermissions.VALIDATE_BVN })
        const { data, status } = response
        if (status === 200 && data.result) {
          this.bvnInformationDetails = {
            ...data.result,
            dateOfBirth: data.result.dateOfBirth ? toDateWithYear(data.result.dateOfBirth) : null,
            registrationDate: data.result.registrationDate ? toDateWithYear(data.result.registrationDate) : null,
          }
          this.showSpinner = false
          this.isLoading = false
          this.$root.$emit('bv::show::modal', 'bvn-validation-modal')
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.showSpinner = false
        this.isLoading = false
      }
    },
    handleContinue() {
      this.virtualAccountFormData = {
        ...this.virtualAccountFormData,
        title: this.bvnInformationDetails.title,
        bvn: this.bvnInformationDetails.bvn,
        firstName: this.bvnInformationDetails.firstName,
        lastName: this.bvnInformationDetails.lastName,
        otherNames: this.bvnInformationDetails.middleName,
        address: this.bvnInformationDetails.residentialAddress,
        gender: this.bvnInformationDetails.gender,
        maritalStatus: this.bvnInformationDetails.maritalStatus,
        phoneNumber: this.bvnInformationDetails.phoneNumber1,
        notificationPhoneNumber: this.bvnInformationDetails.phoneNumber1,
        dateOfBirth: this.bvnInformationDetails.dateOfBirth,
        email: this.bvnInformationDetails.email,
        notificationEmail: this.bvnInformationDetails.email,
        nationality: this.bvnInformationDetails.nationality,
        state: this.bvnInformationDetails.stateOfOrigin,
        lga: this.bvnInformationDetails.lgaOfOrigin,
        hometown: this.bvnInformationDetails.lgaOfResidence,
      }
    },
    backNavigationAction() {
      this.showBackButton = true
      this.$router.push({
        name: 'current-savings-account-list-nib',
      })
    },
  },
}
</script>
<style lang="scss">
 @import '@core/scss/vue/libs/vue-wizard.scss';
  @import '@core/scss/vue/libs/vue-select.scss';
 [dir] .demo-inline-spacing > *, .custom-control , .custom-control-inline, .custom-checkbox {
    margin-top: 0rem !important;
}
.table-responsive {
    padding-bottom: 100px !important;
}
.upload-image{
    border: 2px dashed #DCD9D9;
    position: relative;
    width: 250px;
    height: 250px
}
.format {
    color: #999999 !important;
}
form input[type=file]{
  position: absolute;
  margin: 0;
  padding: 0;
  width: 100%;
  height: 100%;
  outline: none;
  opacity: 0;
}
.upload-instruction{
  padding-top: 22%;
  color: #000;
  text-align: center;
}
.trash {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 1;
  background: #fff;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 10px;
  margin-right: 10px;
  cursor: pointer;
}
.view-details {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  background: #fff;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 10px;
  margin-left: 10px;
  cursor: pointer;
}
</style>
